/**
 * Central registry of page documentation.
 * Each page's data sources, calculations, limitations, and methodology.
 * Used by AboutThisPage component and potentially the AI Data Analyst.
 *
 * Keys (#729): either a plain pathname ("/materials") documenting the whole
 * Page, or a tab-qualified key ("/materials?tab=inventory") documenting one
 * Tab of a merged Page. Always resolve through `resolvePageDoc`: it picks the
 * most specific doc for a (path, tab) pair and canonicalises retired paths
 * through the #719 alias map so old bookmarks still find their doc.
 */

import { canonicalPath } from "./rbac";
import { PATH_ALIASES } from "./sidebar-pages";
import {
  OSTENDO_LIVE_CEILING_PER_HOUR,
  PRODUCT_INVENTORY_LOADS_WORD,
  PRODUCT_INVENTORY_STATEMENT_LEGS,
  PRODUCT_INVENTORY_STATEMENTS_WORD,
} from "./product-inventory/query-cost";

export interface DataSource {
  name: string;
  table?: string;
  source: string;
  refresh?: string;
}

export interface Calculation {
  metric: string;
  formula: string;
  notes?: string;
}

export interface PageDocumentation {
  title: string;
  description: string;
  dataSources: DataSource[];
  calculations: Calculation[];
  limitations: string[];
  version: string;
  lastUpdated: string;
}

/**
 * What one load of Product Inventory costs the app-wide live Ostendo window.
 * Every count is interpolated from query-cost.ts, the table the endpoint takes
 * its per-leg company lists from, so this sentence cannot drift from what the
 * endpoint issues.
 */
const COMPANY_NAME: Record<string, string> = { CROS: "Crosbe National", COOP: "Coopers" };
const PRODUCT_INVENTORY_COST =
  `Live Ostendo reads share a ceiling of ${OSTENDO_LIVE_CEILING_PER_HOUR} queries an hour across the whole app, and one load of this page spends ${PRODUCT_INVENTORY_STATEMENTS_WORD}: ` +
  PRODUCT_INVENTORY_STATEMENT_LEGS
    .map((leg) => `${leg.phrase} on ${leg.dbs.length > 1 ? "both companies" : COMPANY_NAME[leg.dbs[0]]}`)
    .join(", ") +
  `, with the batch-level read scoped to the 38 board item codes. That is ${PRODUCT_INVENTORY_LOADS_WORD} uncached loads to the hour for the whole app. The QC release and schedule reads use other databases and do not count against it`;

export const PAGE_DOCS: Record<string, PageDocumentation> = {
  "/": {
    title: "Overview",
    description:
      "Plant-wide KPI dashboard with AI-generated executive summary. Shows aggregated metrics from production, quality, materials, lab, and cost systems.",
    dataSources: [
      {
        name: "Production summary",
        table: "production_monitor_batch",
        source: "Cloud SQL (live)",
        refresh: "~15 minutes via NeoNetworks sniffer",
      },
      {
        name: "Material variance",
        table: "production_monitor_batchmaterial",
        source: "Cloud SQL (live)",
      },
      {
        name: "Lab overview",
        table: "crosbe_sample, crosbe_certificate",
        source: "Cloud SQL (live)",
      },
      {
        name: "Predictive alerts",
        table: "qa_production_*sample tables",
        source: "Cloud SQL (live)",
      },
      {
        name: "Cost summary",
        table: "raw_material_costs, material_cost_map (D1)",
        source: "Ostendo RM Listing CSV (June 2025)",
      },
      {
        name: "AI plant status",
        source: "Claude Haiku, called on page load",
        refresh: "Cached for 15 minutes",
      },
    ],
    calculations: [
      {
        metric: "Daily Throughput",
        formula: "SUM(weight_kg) / (date_range_days + 1)",
        notes:
          "Divides by calendar days, not production days. Weekends/shutdowns lower the average.",
      },
      {
        metric: "Material Variance",
        formula:
          "AVG(ABS((actual_kg - target_kg) / target_kg × 100)) across all materials",
        notes: "Only materials with target_kg > 0 included.",
      },
      {
        metric: "Material Cost / Tonne",
        formula: "Σ(actual_kg × std_cost) / (total_production_kg / 1000)",
        notes: "Uses standard cost. 33 of 42 materials mapped.",
      },
      {
        metric: "AI Plant Status",
        formula:
          "Claude Haiku analyses last 7 days of production (batches, tonnes), top 5 material waste variances (30 days), and QA sample counts",
        notes:
          "AI-generated text. May occasionally include markdown formatting. Fixed 7-day production window.",
      },
    ],
    limitations: [
      "AI summary is generated fresh each time (15-min cache). Content may vary between loads.",
      "Production data has ~15-minute delay from factory floor.",
      "Previous period comparison uses the same duration shifted back (e.g. 90 days → previous 90 days).",
    ],
    version: "v2.0",
    lastUpdated: "2026-03-27",
  },

  "/costs": {
    title: "Cost Analysis",
    description:
      "Raw material cost tracking using Ostendo pricing data cross-referenced with live production material usage.",
    dataSources: [
      {
        name: "Production material usage",
        table: "production_monitor_batchmaterial JOIN production_monitor_material",
        source: "Cloud SQL (live)",
        refresh: "~15 minutes",
      },
      {
        name: "Material costs",
        table: "raw_material_costs (D1)",
        source: "Ostendo RM Listing CSV import",
        refresh: "Manual import (June 2025)",
      },
      {
        name: "Material-cost mapping",
        table: "material_cost_map (D1)",
        source: "Fuzzy match + manual mapping",
      },
      {
        name: "Production weight",
        table: "production_monitor_batch.weight_kg",
        source: "Cloud SQL (live)",
      },
    ],
    calculations: [
      {
        metric: "Total Material Spend",
        formula: "Σ(actual_kg × std_cost) for all mapped materials in the date range",
        notes:
          "Cross-database: postgres for usage (actual_kg), D1 for costs (std_cost). Multiplied in JavaScript.",
      },
      {
        metric: "Cost per Tonne",
        formula: "total_spend / (total_production_kg / 1000)",
        notes: "total_production_kg from SUM(weight_kg) in production_monitor_batch.",
      },
      {
        metric: "Avg Cost per Batch",
        formula: "total_spend / total_batch_count",
        notes:
          "Direct count of all production batches in the period from production_monitor_batch.",
      },
      {
        metric: "Waste Cost",
        formula: "Σ(|actual_kg - target_kg| × std_cost) for all mapped materials",
        notes: "Only materials with target_kg > 0. Absolute variance × unit cost.",
      },
      {
        metric: "Previous Period Comparison",
        formula:
          "Same calculation applied to (from - period_length) to (from - 1 day)",
        notes: "Period length = to_date - from_date + 1 days.",
      },
      {
        metric: "Supplier Risk %",
        formula: "single_source_count / total_materials × 100",
        notes:
          "Based on supplier and alternate_supplier fields from Ostendo CSV.",
      },
      {
        metric: "Cost Waterfall",
        formula: "Σ(actual_kg × std_cost) grouped by material for a single product",
        notes: "Filtered by product_lookup exact match.",
      },
    ],
    limitations: [
      "9 of 42 production materials have no cost mapping. These are internally produced masterbatches and rework material.",
      "Costs are from the June 2025 Ostendo RM Listing and may not reflect current supplier pricing.",
      "Raw material costs only: no transport, energy, labour or packaging.",
      "Cost basis defaults to std_cost. Toggle to switch between standard, last purchase, and new proposed costs.",
      "Cross-database join (postgres + D1) means there's no atomic consistency guarantee, though in practice this is negligible.",
    ],
    version: "v2.0",
    lastUpdated: "2026-03-27",
  },

  "/carbon": {
    title: "Carbon Footprint",
    description:
      "Indicative embodied carbon (CO2e) per product calculated from formulation BOMs multiplied by published emission factors. Scope A1-A3 (cradle-to-gate).",
    dataSources: [
      {
        name: "Formulation BOMs",
        table: "production_monitor_formulation + formulationmaterial + material",
        source: "Cloud SQL (live)",
        refresh: "Real-time (CURRENT formulations only)",
      },
      {
        name: "Emission factors",
        table: "emission_factors (D1)",
        source: "AusLCI v1.42, MECLA, ICE Database, literature estimates",
        refresh: "Static (manually maintained)",
      },
    ],
    calculations: [
      {
        metric: "CO2e per Tonne of Product",
        formula:
          "Σ(ingredient_kg / 1000 × emission_factor_per_tonne) / (batch_weight_kg / 1000) × 1000",
        notes:
          "For each ingredient: convert kg to tonnes, multiply by factor, sum all. Then normalise to per-tonne of finished product.",
      },
      {
        metric: "Ingredient CO2 Contribution %",
        formula: "ingredient_co2 / total_product_co2 × 100",
      },
      {
        metric: "Confidence Rating",
        formula:
          "Overall confidence = lowest confidence of any ingredient. High (AusLCI) > Medium (literature) > Low > Estimate.",
      },
    ],
    limitations: [
      "INDICATIVE only. Not verified for EPD registration.",
      "GP Cement factor (967 kg CO2e/t) is for finished cement, not clinker. Source: AusLCI v1.42.",
      "CAC (Calcium Aluminate Cement) uses an estimated factor of 600 kg CO2e/t. The actual value needs a supplier EPD from Imerys/Kerneos.",
      "Specialty chemicals (HPMC, Vinnapas, polymers) use rough estimates (2,000-5,000 kg CO2e/t). No published EPDs exist.",
      "Scope is A1-A3 only: raw material extraction + transport to factory + manufacturing. Does NOT include Crosbe's own energy use, transport to customer, or end-of-life.",
      "Dust and rework materials assigned zero CO2 burden (recovered waste).",
      "GGBFS (slag) factor of 192 kg CO2e/t uses AusLCI economic allocation. International sources using mass allocation give 50-80 kg CO2e/t.",
    ],
    version: "v2.0",
    lastUpdated: "2026-03-27",
  },

  "/health": {
    title: "Plant Health Scorecard",
    description:
      "Composite plant health score (0-100%) inspired by Xero Business Health. Groups key operational metrics with targets, period comparison, and industry benchmarks.",
    dataSources: [
      {
        name: "Production metrics",
        table: "production_monitor_batch",
        source: "Cloud SQL (live)",
      },
      {
        name: "Material waste",
        table: "production_monitor_batchmaterial",
        source: "Cloud SQL (live)",
      },
      {
        name: "Lab turnaround",
        table: "crosbe_certificate, crosbe_testrequest, crosbe_sample",
        source: "Cloud SQL (live)",
      },
      {
        name: "Cost data",
        table: "raw_material_costs, material_cost_map (D1)",
        source: "Ostendo CSV (June 2025)",
      },
      {
        name: "Industry benchmarks",
        source: "AusLCI, MECLA, ICE Database, Sika Annual Report 2025, Six Sigma standards",
      },
    ],
    calculations: [
      {
        metric: "Plant Health Score",
        formula:
          "Weighted average: Σ(metric_score × importance_weight) / Σ(importance_weight). High=3x, Medium=2x, Low=1x. Pass=100, Warning=60, Fail=20.",
        notes: "Only metrics with explicit targets contribute to the score.",
      },
      {
        metric: "Daily Throughput Target",
        formula: "50 tonnes/day",
        notes: "Internal target. Status: ≥50t = pass, ≥40t = warning, <40t = fail.",
      },
      {
        metric: "Material Waste Target",
        formula: "<2.0% average absolute variance",
        notes:
          "Industry benchmark: batch plants 0.3-1.5% (concrete). Specialty products: 2-3% is good, <2% is excellent.",
      },
      {
        metric: "Cost per Tonne Target",
        formula: "<$120/tonne",
        notes:
          "Internal target. Sika (proxy): materials ~45% of revenue. Global grout average: USD 802/tonne.",
      },
      {
        metric: "Lab Turnaround Target",
        formula: "<14 days from sample receipt to certificate",
        notes:
          "Includes curing time (7d/28d tests are inherent). 1-2 business days post-cure is expected.",
      },
      {
        metric: "Period Comparison",
        formula:
          "Same metrics calculated for (from - compare_period) to (from - 1). Compare options: 1 month, 3 months, 1 year.",
      },
    ],
    limitations: [
      "Targets are internal estimates. No industry-specific targets for specialty grout manufacturing exist.",
      "Lab turnaround includes inherent curing periods (7d, 28d) which cannot be reduced.",
      "Cost per tonne target of $120 is approximate and varies significantly by product mix.",
      "Health score weights are subjective (High=3x, Medium=2x, Low=1x).",
      "Changeover count is reported but not included in the health score calculation.",
    ],
    version: "v2.0",
    lastUpdated: "2026-03-27",
  },

  "/flush-waste": {
    title: "Flush Waste",
    description:
      "Flush waste attributed to assembly orders, shown against BOM and actual consumption per material, so flush can be priced into true landing cost instead of the flat ~2% BOM allowance. Pick a period (the financial year to date by default); Export to Excel downloads the product, assembly order and unattributed lines for that period.",
    dataSources: [
      { name: "FLUSH WASTE stock adjustments", source: "Ostendo COOP InventoryAdjustments + InventoryAdjustLines" },
      { name: "Assembly order recipes and actuals", source: "Ostendo COOP AssemblyLines (ORDERQTY = BOM plan, TOTALISSUEDQTY = actual)" },
      { name: "Assembly order identity", source: "Ostendo COOP AssemblyHeader (product, order qty, status)" },
    ],
    calculations: [
      {
        metric: "AO attribution",
        formula: "Parsed from the adjustment's free-text reference (single AO, AO range like AO4002741-4, or explicit list). When the reference names no AO, an AO written in the line notes is used instead. Range or list members missing from AssemblyHeader take no share.",
        notes: "Lines that name no existing assembly order stay visible in the No AO row under the product tree, so totals always reconcile with Ostendo. Positive adjustment lines (corrections) NET against write-offs, so an AO can show negative flush.",
      },
      {
        metric: "Range/list allocation",
        formula: "Even = kg ÷ AO count. Weighted = kg × (this AO's planned input kg ÷ span total planned input kg)",
        notes: "Weighted deliberately uses planned input kg from recipe rows, not pack-size × output quantity.",
      },
      {
        metric: "Δ BOM→Actual+Flush",
        formula: "actual + allocated flush − BOM plan",
        notes: "The target number: true consumption including flush, per material.",
      },
    ],
    limitations: [
      "Quantities only in v1. No $ costing yet.",
      "Unposted assembly orders show BOM only (actuals are 0 until posted in Ostendo).",
      "The period filters flush lines by their adjustment date. An assembly order's detail panel still shows all of its flush lines.",
      "The split toggle applies to each assembly order's per-material detail only. Product and assembly order totals always split a shared flush line evenly.",
      "Access: admin, CEO/Operations and finance roles.",
    ],
    version: "v1.1",
    lastUpdated: "2026-10-08",
  },
  "/report": {
    title: "Executive Report",
    description:
      "Print-optimised single-page plant performance report. Aggregates KPIs, charts, AI summary, and top materials table for board handout. Pick a period (the last 90 days by default); Export to Excel downloads the KPIs and tables for that period.",
    dataSources: [
      { name: "All data sources from Overview page", source: "See Overview documentation" },
      { name: "All data sources from Cost Analysis page", source: "See Cost Analysis documentation" },
      { name: "Cost of sales variances", table: "JournalHeader + JournalLines (joined on JOURNALNUMBER)", source: "Ostendo COOP, the postings it sends to QuickBooks" },
    ],
    calculations: [
      {
        metric: "Cost of sales variances",
        formula: "SUM(DEBITVALUE) minus SUM(CREDITVALUE) per account, by the month of each journal's JOURNALDATE",
        notes: "The eight 55000 Standard Cost Variance and 56000 Stock Adjustments accounts, matched on the account name in LEDGERCODE, with a total per group. Only journals with status Transaction Valid count. A negative figure is a credit balance, as on the QuickBooks P&L. \"Last changed\" under each month is the latest SYSDATEMODIFIED across the headers and lines of every journal dated in that month, so a late posting shows as a newer date. Each figure opens the journal lines behind it.",
      },
      {
        metric: "All KPIs",
        formula: "Same calculations as Overview and Cost Analysis pages",
        notes: "Report uses the same API endpoints and your saved Cost Analysis cost basis, so the same period gives the same numbers on both pages.",
      },
      {
        metric: "AI Executive Summary",
        formula: "Same as Overview AI Plant Status",
        notes: "Strips markdown headings from AI output. May regenerate on each load (15-min cache).",
      },
    ],
    limitations: [
      "Report is a snapshot at load time and does not auto-refresh.",
      "Print layout may vary by browser. Tested in Chrome.",
      "Charts render as SVG, which prints well. No rasterisation issues.",
      "If the AI summary cannot be generated, that card says so and the rest of the report still shows.",
      "Cost of sales variances are Ostendo postings only: a price difference entered directly in QuickBooks is not included. The section covers a period of a year or less, and shows for the finance, CEO/Ops and admin roles.",
    ],
    version: "v2.2",
    lastUpdated: "2026-10-15",
  },

  "/optimizer": {
    title: "Formulation Optimiser",
    description:
      "What-if simulator for formulation changes. Adjust ingredient percentages to see instant impact on cost and carbon footprint.",
    dataSources: [
      {
        name: "Formulation BOMs",
        table: "production_monitor_formulation (CURRENT status) + formulationmaterial",
        source: "Cloud SQL (live)",
      },
      {
        name: "Material costs",
        table: "material_cost_map + raw_material_costs (D1)",
        source: "Ostendo CSV (June 2025)",
      },
      {
        name: "Emission factors",
        table: "emission_factors (D1)",
        source: "AusLCI v1.42, literature, estimates",
      },
    ],
    calculations: [
      {
        metric: "Adjusted Weight",
        formula: "new_kg = (base_pct + adjustment) / 100 × batch_weight_kg",
        notes: "Adjustment is in percentage points. Clamped to min 0%.",
      },
      {
        metric: "Adjusted Cost",
        formula: "new_kg × cost_per_kg for each material, then sum",
      },
      {
        metric: "Adjusted CO2",
        formula: "(new_kg / 1000) × emission_factor for each material, then sum",
      },
      {
        metric: "Cost per Tonne (Adjusted)",
        formula: "(total_adjusted_cost / total_adjusted_kg) × 1000",
      },
      {
        metric: "% Change",
        formula: "((adjusted - original) / original) × 100",
      },
    ],
    limitations: [
      "This tool shows cost and carbon impact ONLY. It does not validate technical feasibility.",
      "Ingredient adjustments are independent, so the total percentage may not sum to 100%.",
      "A warning (⚠) appears when the total deviates more than 0.5% from 100%.",
      "Performance constraints (strength, flow, setting time) are NOT checked.",
      "Only products with CURRENT formulations are available.",
    ],
    version: "v2.0",
    lastUpdated: "2026-03-27",
  },

  "/production": {
    title: "Production Dashboard",
    description:
      "Batch production monitoring with a ranked product-family leaderboard, a weekly history range strip, product mix and batch-level material detail.",
    dataSources: [
      {
        name: "Batch data",
        table: "production_monitor_batch",
        source: "Cloud SQL (live)",
        refresh: "~15 minutes via NeoNetworks sniffer from factory",
      },
      {
        name: "Batch materials",
        table: "production_monitor_batchmaterial + production_monitor_material",
        source: "Cloud SQL (live)",
      },
    ],
    calculations: [
      {
        metric: "Total Production",
        formula: "SUM(weight_kg) WHERE production_time IN date range",
      },
      {
        metric: "Production by family",
        formula: "Sum of batch weight in the selected Sydney dates, grouped by product family. Share = family tonnes / selected total tonnes.",
        notes: "The eight largest families over all history keep fixed colours. Other families share one unranked grey row. Rows rank the coloured families within the selected dates. The strip shows weekly tonnes; the 7-day comparison ends on the selected end date.",
      },
      {
        metric: "Previous Period",
        formula: "Same metrics for (from - period_length) to (from - 1)",
        notes: "Trend arrow shows % change vs previous period.",
      },
      {
        metric: "Material Variance (per batch)",
        formula: "((actual_kg - target_kg) / target_kg) × 100",
        notes: "Only shown for materials with target_kg > 0.",
      },
    ],
    limitations: [
      "Production data has ~15-minute delay from the factory floor.",
      "Established QA name patterns take precedence, followed by eight exact production aliases verified by R&D. Unmapped production names remain in the grey presentation group with an expandable list of every name and its tonnes; they are not treated as a confirmed family.",
      "Batch weight (weight_kg) is the finished product weight, not total material weight.",
      "product_lookup is a free-text field with 72 distinct values, including seasonal variants, rework blends and trials.",
    ],
    version: "v2.1",
    lastUpdated: "2026-10-08",
  },

  "/shifts": {
    title: "Shift Analysis",
    description:
      "Production patterns by hour, day of week, and shift, in Sydney time. Shows the plant's two-shift day and any batches outside it.",
    dataSources: [
      {
        name: "Production timestamps",
        table: "production_monitor_batch.production_time",
        source: "Cloud SQL (live)",
      },
    ],
    calculations: [
      {
        metric: "Hourly Pattern",
        formula: "COUNT(*) GROUP BY EXTRACT(hour FROM (production_time AT TIME ZONE 'Australia/Sydney'))",
        notes: "Every hour on this page is Sydney time.",
      },
      {
        metric: "Day-of-Week Pattern",
        formula: "COUNT(*) GROUP BY EXTRACT(dow FROM (production_time AT TIME ZONE 'Australia/Sydney'))",
        notes: "0 = Sunday, 6 = Saturday.",
      },
      {
        metric: "Heatmap",
        formula: "COUNT(*) GROUP BY (dow, hour), a 7 × 24 grid",
      },
      {
        metric: "Morning",
        formula: "SUM(batches) WHERE hour >= 5 AND hour < 13",
        notes: "About 05:00-13:00. The changeover around 13:00 moves, so the split is approximate.",
      },
      {
        metric: "Afternoon",
        formula: "SUM(batches) WHERE hour >= 13 AND hour < 21",
        notes: "About 13:00-21:00. The busier of Morning and Afternoon is highlighted.",
      },
      {
        metric: "Outside 05:00-21:00",
        formula: "SUM(batches) WHERE hour < 5 OR hour >= 21",
        notes: "Morning + Afternoon + Outside = Total Batches.",
      },
      {
        metric: "Product Changeovers",
        formula: "COUNT(*) WHERE product_lookup != LAG(product_lookup) ORDER BY production_time",
        notes: "Each change of product_lookup between consecutive batches counts as one changeover.",
      },
    ],
    limitations: [
      "The plant day (Monday to Friday, about 05:00 to 21:00 Sydney time) comes from the production schedule, June to October 2026. The 05:00 start and 21:00 finish are firm; the 13:00 changeover is approximate.",
      "Shifts are counted by the hour a batch was produced, not by crew or roster, so a batch near 13:00 can fall on either side.",
    ],
    version: "v2.1",
    lastUpdated: "2026-10-27",
  },

  // #1587: Latest Tests un-merged back to its own page (/latest-tests,
  // "Recent Results" in Gus's menu). The doc content moved with it, and
  // /quality keeps the analytics tabs.
  "/latest-tests": {
    title: "Recent Results",
    description:
      "QC sample entries created or updated recently, newest first and grouped by day. The review view for seeing yesterday's lab work at a glance, with any row expandable for the full sample detail.",
    dataSources: [
      {
        name: "QC sample entries (all products)",
        table: "qa_production_*sample tables",
        source: "Cloud SQL (live)",
      },
      {
        name: "Compressive-strength spec bands",
        source: "QA configuration (per product, per test age)",
      },
    ],
    calculations: [
      {
        metric: "Result badge",
        formula: "latest available compressive-strength age (28d, else 7d, 3d, 24h) graded against that age's spec band",
        notes: "The graded age is shown on the badge so an early-age pass isn't read as a final 28-day pass.",
      },
      {
        metric: "Figures (MPa)",
        formula: "recorded A/B average at each strength age, graded unrounded against that age's active spec band, shown as recorded",
        notes: "Each age carries its own colour; the age the Result badge was graded on is dotted-underlined.",
      },
      {
        metric: "Flagged only",
        formula: "server-side filter to entries whose Result is Near limit or Out of spec",
        notes: "The count on the toggle is for the whole window, whichever view is showing. The default view is always the full stream.",
      },
      {
        metric: "New vs Updated",
        formula: "New when the entry was created in the window; Updated when an existing entry was modified",
      },
    ],
    limitations: [
      "Compressive strength is the graded result. Other measurements appear in the detail view but don't drive the badge.",
      "Samples with results but no configured spec show a dash instead of a grade.",
      "Days group by Australia/Sydney local date; the lookback is capped at 90 days.",
      "If a product's sample table cannot be read, the page says so (N of M products unavailable) rather than showing a quieter stream.",
    ],
    version: "v1.2",
    lastUpdated: "2026-09-03",
  },

  // The merged analytics hub: a whole-Page doc plus one keyed doc per tab
  // (same shape as /raw-materials). The tab keys match QCTestsHub's TABS.
  "/quality": {
    title: "Finished Goods",
    description:
      "The finished-goods QC analytics panel opens on Control Charts, with Records as the default view after choosing a product. Recently entered samples live on their own page, Recent Results.",
    dataSources: [
      {
        name: "QA sample data",
        table: "qa_production_{slug}sample (15 product tables)",
        source: "Cloud SQL (live)",
      },
      {
        name: "Spec limits",
        table: "qa_production_{slug}config",
        source: "Cloud SQL (live)",
      },
    ],
    calculations: [
      {
        metric: "Tab metrics",
        formula: "each tab documents its own calculations: open the tab's Info panel",
      },
    ],
    limitations: [
      "This whole-page doc covers the container; per-tab sources, formulas and limits live on each tab's own doc.",
    ],
    version: "v2.1",
    lastUpdated: "2026-08-16",
  },

  "/quality?tab=charts": {
    title: "Control Charts",
    description:
      "Statistical Process Control (SPC) charts for compressive strength, flow, and setting time across 15 QA products. Includes Nelson Rules anomaly detection and Cpk process capability.",
    dataSources: [
      {
        name: "QA sample data",
        table: "qa_production_{slug}sample (15 product tables)",
        source: "Cloud SQL (live)",
      },
      {
        name: "Spec limits",
        table: "qa_production_{slug}config",
        source: "Cloud SQL (live)",
      },
    ],
    calculations: [
      {
        metric: "Control Limits",
        formula: "UCL = mean + 3σ, LCL = mean - 3σ (sample standard deviation, N-1)",
        notes: "σ is STDDEV() from PostgreSQL (sample stddev).",
      },
      {
        metric: "Cpk",
        formula: "MIN((USL - μ) / 3σ, (μ - LSL) / 3σ)",
        notes: "Computed client-side from SPC stats. <1.0 = incapable, 1.33 = minimum, 2.0+ = excellent.",
      },
      {
        metric: "Nelson Rules",
        formula: "Rules 1 (beyond 3σ), 2 (9 same side), 3 (6 trending), 5 (2 of 3 beyond 2σ), 6 (4 of 5 beyond 1σ)",
        notes: "Computed server-side in nelson-rules.ts. Shared between SPC and Insights pages.",
      },
      {
        metric: "Strength Average",
        formula: "(compressive_strength_{age}_a + compressive_strength_{age}_b) / 2",
        notes: "Duplicate cube readings averaged. Column names use _a/_b suffixes.",
      },
    ],
    limitations: [
      "Not all products have all test columns. The API handles missing columns with try/catch.",
      "Strength time points vary by product (most have 24h/7d/28d; the full set of available columns is discovered dynamically per product).",
      "Nelson Rules use a minimum of 9 data points, so products with fewer samples show no alerts.",
      "No direct FK between production batches and QA samples, so correlation is date-based (approximate).",
    ],
    version: "v2.0",
    lastUpdated: "2026-03-27",
  },

  "/material-sampling": {
    title: "Material Sampling",
    description:
      "Sampling practice vs the L0/P/EOR rule: material flows, live lot/state coverage, and whether QC sampled at every material change, not just at a comfortable volume.",
    dataSources: [
      {
        name: "Material state ledger",
        table: "material_state (D1, synced from Ostendo InventoryTransactions)",
        source: "D1 (daily sync)",
      },
      {
        name: "Production orders (AOs)",
        table: "ItemBatches × ItemMaster",
        source: "Ostendo COOP snapshot",
      },
      {
        name: "QC samples (hard-keyed via AO tail)",
        table: "qa_production_upi + per-product sample tables",
        source: "Cloud SQL (live)",
      },
    ],
    calculations: [
      {
        metric: "Coverage %",
        formula: "states_sampled / states_consumed × 100",
        notes: "A state counts as sampled when ≥1 AO produced while it was live has a QC sample (hard-keyed first; honest time-window degrade, labelled).",
      },
      {
        metric: "Rule verdicts",
        formula: "L0 (first AO after any state change) + P10/P50 (every Nth AO in a stable state) + EOR (final AO of a run)",
        notes: "A derived bulk-delivery boundary yields 'unknowable', never laundered into satisfied. Grain is production orders (AOs), never weigh batches.",
      },
    ],
    limitations: [
      "Bulk trio (silo) state boundaries are derived delivery intervals until the operator-visible changeover identifier question is answered, so their L0 verdicts read 'unknowable'.",
      "The AO tail key's reach is reported as linkage_rate; where absent, the engine degrades to a labelled time-window join.",
      "Ostendo is a ~daily snapshot, so the newest AOs may not be visible yet.",
    ],
    version: "v1.0",
    lastUpdated: "2026-08-04",
  },

  "/materials": {
    title: "Materials Variance",
    description:
      "Target vs actual material usage variance across all 42 production materials. Shows which materials have the most waste.",
    dataSources: [
      {
        name: "Batch material usage",
        table: "production_monitor_batchmaterial + batch + material",
        source: "Cloud SQL (live)",
      },
    ],
    calculations: [
      {
        metric: "Average Absolute Variance %",
        formula: "AVG(ABS((actual_kg - target_kg) / target_kg × 100))",
        notes: "Grouped by material. Only materials with target_kg > 0.",
      },
      {
        metric: "Trend (daily)",
        formula: "AVG(target_kg) and AVG(actual_kg) GROUP BY date for a specific material",
      },
    ],
    limitations: [
      "Variance is always absolute (positive). The summary doesn't distinguish over-use from under-use.",
      "Materials with very small target quantities may show high percentage variance from small absolute differences.",
    ],
    version: "v2.0",
    lastUpdated: "2026-03-27",
  },

  "/intelligence": {
    title: "Plant Intelligence",
    description:
      "Deep data stories: 6-year seasonal strength patterns, waste recovery (circular economy), and lab customer concentration analysis.",
    dataSources: [
      {
        name: "QA strength history",
        table: "qa_production_{slug}sample",
        source: "Cloud SQL (live)",
        refresh: "All historical data since 2020",
      },
      {
        name: "Waste recovery materials",
        table: "production_monitor_batchmaterial WHERE material LIKE 'Dust%' OR 'Rework%'",
        source: "Cloud SQL (live)",
      },
      {
        name: "Lab customers",
        table: "crosbe_customer + crosbe_contactperson + crosbe_sample",
        source: "Cloud SQL (live)",
      },
    ],
    calculations: [
      {
        metric: "Seasonal Strength",
        formula: "AVG((strength_7d_a + COALESCE(strength_7d_b, strength_7d_a)) / 2) GROUP BY month",
        notes: "Monthly average across all samples. Products selectable via dropdown.",
      },
      {
        metric: "Waste Recovery",
        formula: "SUM(actual_kg) WHERE material IN ('Dust', 'Rework 830', 'Rework PT Grout')",
        notes: "Zero-cost materials reused in production. Circular economy metric.",
      },
      {
        metric: "Customer Concentration (Pareto)",
        formula: "COUNT(samples) per customer, ordered DESC, with cumulative %",
        notes: "Top 2 customers represent ~50% of all lab samples.",
      },
    ],
    limitations: [
      "Seasonal strength patterns reflect QA sampling frequency, not every batch produced.",
      "Summer/winter strength variation is well-known in cement chemistry (temperature affects hydration rate).",
      "Customer concentration is based on lab sample count, not revenue or order value.",
      "Waste recovery only captures Dust and Rework materials that are re-batched. Other waste streams are not tracked.",
    ],
    version: "v2.0",
    lastUpdated: "2026-03-27",
  },

  "/cockpit": {
    title: "Operations Traceability",
    description:
      "End-to-end batch traceability spine: enter a customer order and see its full path from raw-material purchase order to customer, left-to-right, with honest per-stage state badges. Read-only, query-on-demand.",
    dataSources: [
      {
        name: "Order → batch → raw-lot → PO walk",
        table: "Ostendo InventoryTransactions (SALESISSUE / ASSYISSUE / PURCHASERECEIPT) across COOP + CROS",
        source: "Ostendo live (dual-DB, via trace_order)",
      },
      {
        name: "Node-3 BOM vs actual (per material)",
        table: "Ostendo AssemblyLines, keyed by the batch AO (ORDERNUMBER): ORDERQTY = recipe-planned, TOTALISSUEDQTY = actually issued",
        source: "Ostendo live (via cockpit-trace)",
      },
      {
        name: "Node-5 packaging",
        table: "Ostendo BomLines for the finished-goods item codes (Item Code lines, non-Kg unit)",
        source: "Ostendo live (via cockpit-trace)",
      },
      {
        name: "Node-7 QC (Cpk)",
        table: "qa_production_<product>sample joined to qa_production_upi on upi_number_id",
        source: "Cloud SQL (live)",
      },
      {
        name: "Node-9 intercompany edge",
        table: "shared batch number across COOP + CROS InventoryTransactions",
        source: "Ostendo live (dual-DB)",
      },
    ],
    calculations: [
      {
        metric: "Node states",
        formula: "confirmed (hard-keyed evidence) / inferred (derived) / no-result (genuinely un-QC'd) / flag (QC out-of-spec) / gap (out-of-system)",
        notes: "The badges are first-class honesty: a gap or no-result means the data genuinely isn't there, not that the page failed.",
      },
      {
        metric: "Node-3 material variance",
        formula: "actual issued (TOTALISSUEDQTY) − recipe-planned (ORDERQTY), per material, signed; e.g. ICL HES 42,653 / 42,307.56 → +345",
        notes: "Shows every material in the batch's BOM with planned vs actual + the signed delta. Falls back to the raw-lot view if Ostendo's AssemblyLines is briefly unreachable.",
      },
      {
        metric: "Node-7 Cpk",
        formula: "min(Cpu, Cpl) on the higher-n test property, against the product's ERROR limits (qa_production_<product>config *_limit_error)",
        notes: "Production QC only. The NATA contractor lab is excluded. No production-QC row → no-result.",
      },
      {
        metric: "Node-9 edge style",
        formula: "solid = post-cutover (COOP batch number carried verbatim to CROS, hard-keyed); dashed = pre-cutover (28-Jan-2026), customer link is qty+date FIFO-probabilistic",
        notes: "The solid/dashed split is the cockpit's core honesty mechanism: it distinguishes a hard key from a probabilistic reconstruction.",
      },
    ],
    limitations: [
      "Order anchor only in v1 (customer/delivery order). Batch and raw-lot anchors are roadmap.",
      "Pre-cutover batches (before 28-Jan-2026) cannot be hard-linked to customers, so nodes 9/10 render dashed and customers are FIFO-inferred, not listed.",
      "Nodes 2 (raw-material approval) and 4 (palletiser) are genuine out-of-system gaps. No source exists, so they render as gap boxes, not failures.",
      "Node-7 QC keys on the AO tail (the digits after AO400); batches with no production-QC row render no-result (honest gap, not missing data).",
      "Node-3 shows BOM-planned vs Ostendo-issued. A third 'plant-weighed' column (from the production-monitor system) is not yet shown. There's no hard key joining a plant batch to its Ostendo AO, so it can't be matched deterministically.",
      "Any node's detail can be opened directly via a deep link: /cockpit?order=<DO#>&node=<N>.",
    ],
    version: "v1.4",
    lastUpdated: "2026-06-15",
  },

  "/material-network": {
    title: "Material Network",
    description:
      "Interactive force-directed graph showing which materials co-occur in the same production batches. Reveals supply chain dependencies and material clusters.",
    dataSources: [
      {
        name: "Material co-occurrence",
        table: "production_monitor_batchmaterial (self-join on batch_id)",
        source: "Cloud SQL (live)",
      },
    ],
    calculations: [
      {
        metric: "Co-occurrence Weight",
        formula: "COUNT(*) WHERE material_A and material_B appear in the same batch (material_id_A < material_id_B)",
        notes: "Top 60 edges shown. Lower threshold materials may not appear.",
      },
      {
        metric: "Node Size",
        formula: "sqrt(total_kg / max_kg) × 40 + 8",
        notes: "Square root scaling prevents dominant materials from overshadowing others.",
      },
      {
        metric: "Material Groups",
        formula: "Hardcoded classification: Core (cement, fly ash, sand), CSA (CAC, anhydrite, walocel), Polymer (HPMC, Mighty), Intermediate (masterbatches), Additive (everything else)",
      },
    ],
    limitations: [
      "Graph layout is physics-simulated (Barnes-Hut), so positions are non-deterministic and may shift on reload.",
      "Only the top 60 co-occurrence edges show, so rare material combinations may not appear.",
      "Material group classification is hardcoded, not derived from chemical properties.",
    ],
    version: "v2.0",
    lastUpdated: "2026-03-27",
  },

  "/product-families": {
    title: "Product Families",
    description:
      "All 72 product variants grouped into families. Shows base products, seasonal variants, rework blends, trials, and exports.",
    dataSources: [
      {
        name: "Production batch product names",
        table: "production_monitor_batch.product_lookup",
        source: "Cloud SQL (live)",
      },
    ],
    calculations: [
      {
        metric: "Family Classification",
        formula: "Pattern matching on product_lookup: SUMMER → seasonal, TRIAL → trial, REWORK → rework, BINDER/MB → intermediate, UK/AUS → export",
        notes: "Heuristic classification. Some edge cases may be miscategorised.",
      },
      {
        metric: "Active Status",
        formula: "last_seen > NOW() - 90 days",
        notes: "Green dot = produced in the last 90 days.",
      },
    ],
    limitations: [
      "Family grouping is heuristic, based on naming patterns in product_lookup, not a formal product hierarchy.",
      "Some products may be grouped differently than intended if naming conventions change.",
      "Batch counts reflect the selected date range. Lifetime totals show when no filter is applied.",
    ],
    version: "v2.0",
    lastUpdated: "2026-03-27",
  },

  "/shifts?tab=changeovers": {
    title: "Changeover Analysis",
    description:
      "Analyses product transitions on the production line. Each changeover is when consecutive batches have different product_lookup values. Frequent changeovers increase batch-1 quality risk.",
    dataSources: [
      {
        name: "Batch sequence",
        table: "production_monitor_batch (ordered by production_time, LAG window function)",
        source: "Cloud SQL (live)",
      },
    ],
    calculations: [
      {
        metric: "Changeover Count",
        formula: "COUNT(*) WHERE product_lookup != LAG(product_lookup) ORDER BY production_time",
        notes: "Each change of product_lookup between consecutive batches is one changeover.",
      },
      {
        metric: "Top Transitions",
        formula: "COUNT GROUP BY (prev_product, current_product) ordered by frequency",
      },
      {
        metric: "Product Frequency",
        formula: "Sum of changeover events where a product appears as either source or target",
      },
    ],
    limitations: [
      "A 'changeover' is defined purely by product_lookup changing between consecutive batches. Some changes may be normal production scheduling, not costly transitions.",
      "Batch-1 quality impact is not yet measured. It needs cross-referencing with QA data (no direct FK exists).",
      "Multiple changeovers within the same hour may represent the same physical transition.",
    ],
    version: "v2.0",
    lastUpdated: "2026-03-28",
  },

  "/portal/:token": {
    title: "Customer Portal",
    description:
      "Customer-facing QC dashboard showing compressive strength trends and results for products the customer buys. Token + optional PIN authentication.",
    dataSources: [
      {
        name: "Portal tokens and customers",
        table: "portal_tokens, portal_customers (D1)",
        source: "Crosbe AI D1 database",
      },
      {
        name: "QA sample data",
        table: "qa_production_{slug}sample (filtered by customer's product slugs)",
        source: "Cloud SQL (live)",
      },
      {
        name: "Production batch count",
        table: "production_monitor_batch (filtered by customer's product LIKE patterns)",
        source: "Cloud SQL (live)",
      },
    ],
    calculations: [
      {
        metric: "Strength Values",
        formula: "(compressive_strength_{age}_a + COALESCE(strength_{age}_b, strength_{age}_a)) / 2",
        notes: "Average of duplicate cube readings. Shows 7d and 28d only.",
      },
      {
        metric: "Recent Batches",
        formula: "COUNT(*) WHERE product_lookup LIKE pattern AND production_time >= NOW() - 90 days",
      },
    ],
    limitations: [
      "Shows ALL QA data for the customer's product types, not customer-specific orders (no order-to-batch mapping exists yet).",
      "Customer-order mapping requires Ostendo CRM integration (pending Cutcher & Neale access).",
      "PIN protection uses SHA-256 hashing. PINs are numeric only (4-8 digits).",
      "Strength values shown are raw test results, not certified values.",
    ],
    version: "v2.0",
    lastUpdated: "2026-03-27",
  },

  "/quality?tab=scorecard": {
    title: "Quality Scorecard",
    description:
      "Traffic-light status matrix across all QA products. Shows which products are performing well and which need attention.",
    dataSources: [
      { name: "QA alerts scan", table: "qa_production_*sample (all 15 product tables)", source: "Cloud SQL (live)" },
    ],
    calculations: [
      { metric: "Grade", formula: "Good = 95% or more of 28d samples in spec. Watch = 90 to 95%. At Risk = under 90%. OK = fewer than 20 samples, or no spec to measure against." },
      { metric: "Cpk", formula: "MIN((USL-μ)/3σ, (μ-LSL)/3σ), computed from 28d compressive strength data", notes: "Shown as a secondary capability indicator; it does not set the grade." },
    ],
    limitations: [
      "Uses 28d compressive strength only. It doesn't assess flow, setting time or other test types.",
      "Products with fewer than 3 samples show no alerts (insufficient data for Nelson Rules).",
      "Grades over a rolling 12 months, so a recent change takes time to move the grade.",
    ],
    version: "v2.1",
    lastUpdated: "2026-08-12",
  },

  "/raw-materials?tab=supplier-quality": {
    title: "Supplier Quality",
    description:
      "Compares raw material suppliers across OPC, CAC, and CSA cements. Shows strength averages, variability (CV%), and active/inactive status.",
    dataSources: [
      { name: "OPC tests", table: "raw_materials_opc", source: "Cloud SQL (live)" },
      { name: "CAC tests", table: "raw_materials_cac", source: "Cloud SQL (live)" },
      { name: "CSA tests", table: "raw_materials_csa", source: "Cloud SQL (live)" },
    ],
    calculations: [
      { metric: "CV% (Coefficient of Variation)", formula: "STDDEV(28d_strength) / AVG(28d_strength) × 100", notes: "Lower = more consistent. <5% green, 5-10% amber, >10% red." },
      { metric: "Active Status", formula: "last_test > NOW() - 180 days" },
    ],
    limitations: [
      "Some suppliers have fewer than 10 tests, so statistical significance is limited.",
      "All OPC comes from ICL, a single supplier dependency.",
    ],
    version: "v2.0",
    lastUpdated: "2026-03-28",
  },

  "/market": {
    title: "Market Intelligence",
    description:
      "Live Australian construction market data from the ABS. Shows engineering construction activity, building approvals, and cement price index.",
    dataSources: [
      { name: "Engineering Construction", table: "ABS EWD dataflow", source: "data.api.abs.gov.au (free, no API key)", refresh: "Quarterly, cached 7 days in KV" },
      { name: "Building Approvals", table: "ABS BA_SA2 dataflow", source: "data.api.abs.gov.au", refresh: "Monthly, cached 7 days" },
      { name: "Producer Price Index", table: "ABS PPI dataflow (codes 8102192 cement, 8102194 sand)", source: "data.api.abs.gov.au", refresh: "Quarterly, cached 7 days" },
    ],
    calculations: [
      { metric: "Engineering values", formula: "Raw ABS values in AUD thousands (displayed as $B)", notes: "Current prices, original series (not seasonally adjusted)." },
      { metric: "Approvals", formula: "Number of dwelling units approved per month" },
      { metric: "PPI", formula: "Index numbers relative to base period (not absolute prices)" },
    ],
    limitations: [
      "ABS data has a reporting lag: engineering construction is ~3 months behind, approvals ~2 months.",
      "PPI is an index, not actual prices. It shows the direction and magnitude of price changes.",
      "Cement and sand indices only. No published PPI for specialty admixtures.",
      "Building approvals are a leading indicator (6-12 months ahead), so correlation with Crosbe orders is approximate.",
    ],
    version: "v2.0",
    lastUpdated: "2026-03-28",
  },

  "/lab": {
    title: "Lab Testing",
    description:
      "NATA-accredited lab operations dashboard. Tracks sample intake, certificate turnaround, customer distribution, and equipment calibration.",
    dataSources: [
      { name: "Samples + certificates", table: "crosbe_sample, crosbe_certificate, crosbe_testrequest", source: "Cloud SQL (live)" },
      { name: "Customers", table: "crosbe_customer, crosbe_contactperson", source: "Cloud SQL (live)" },
      { name: "Equipment", table: "crosbe_equipment, crosbe_equipmentcalibration", source: "Cloud SQL (live)" },
    ],
    calculations: [
      { metric: "Turnaround", formula: "AVG(certificate.created_at - sample.received_on) in days", notes: "Includes inherent curing periods (7d, 28d)." },
      { metric: "SLA colours", formula: "≤7d green, ≤14d amber, >14d red" },
    ],
    limitations: [
      "Turnaround includes curing time, which cannot be reduced: 28d tests inherently take 28+ days.",
      "Calibrations added on this page write to the live equipment tables.",
    ],
    version: "v2.1",
    lastUpdated: "2026-09-03",
  },

  "/certificates": {
    title: "Certificates",
    description:
      "NATA certificate tracking with turnaround trends, customer breakdown, and certificate search.",
    dataSources: [
      { name: "Certificates", table: "crosbe_certificate + crosbe_testrequest + crosbe_sample", source: "Cloud SQL (live)" },
    ],
    calculations: [
      { metric: "Turnaround trend", formula: "AVG days from sample.received_on to certificate.created_at, grouped monthly" },
    ],
    limitations: [
      "Certificate count includes re-issues. Unique test requests may be lower than certificate count.",
    ],
    version: "v2.0",
    lastUpdated: "2026-03-28",
  },

  "/materials?tab=waste": {
    title: "Yield & Waste Analysis",
    description:
      "Material waste tracking with over-use vs under-use breakdown, waste trends by time bucket, and product filtering.",
    dataSources: [
      { name: "Batch materials", table: "production_monitor_batchmaterial + batch + material", source: "Cloud SQL (live)" },
    ],
    calculations: [
      { metric: "Waste %", formula: "ABS(actual_kg - target_kg) / target_kg × 100 per material per batch" },
      { metric: "Over-usage", formula: "SUM(actual_kg - target_kg) WHERE actual > target" },
      { metric: "Under-usage", formula: "SUM(target_kg - actual_kg) WHERE target > actual" },
    ],
    limitations: [
      "Only materials with target_kg > 0 are included.",
      "Absolute variance treats over and under equally: both contribute to waste %.",
    ],
    version: "v2.0",
    lastUpdated: "2026-03-28",
  },

  "/raw-materials": {
    title: "Raw Materials Analytics",
    description:
      "The Analysis tab shows SPC charts with Nelson Rules for incoming raw material tests (OPC cement, CAC, CSA, plasticiser, thickener), and only roles without RM-QC access see it. Roles with RM-QC access open on Records instead, where each test's chart shows Crosbe results, supplier values or both against the spec limits.",
    dataSources: [
      { name: "Raw material tests", table: "raw_materials_opc, raw_materials_cac, raw_materials_csa, raw_materials_plasticizer, raw_materials_thickener", source: "Cloud SQL (live)" },
    ],
    calculations: [
      { metric: "SPC control limits", formula: "UCL = mean + 3σ, LCL = mean - 3σ (same as finished goods QC)" },
      { metric: "Supplier grouping", formula: "product_type column contains the supplier name" },
    ],
    limitations: [
      "Not all raw materials have the same test columns. The API handles missing columns with try/catch.",
      "Supplier names are free-text in product_type, so minor spelling variations may create apparent duplicates.",
    ],
    version: "v2.1",
    lastUpdated: "2026-10-14",
  },

  "/raw-materials/field-manager": {
    title: "RM-QC Field Manager",
    description:
      "Maintain the raw-material QC vocabulary: add Ostendo materials, choose tests for each panel, reorder the panel, and version warning and error limits. For each material, set the bench sheet's header fields (which fields, their order and their labels) and the section each test sits under.",
    dataSources: [
      {
        name: "Raw-material vocabulary",
        table: "rm_material, rm_test_definition, rm_material_test",
        source: "Live production records",
      },
      {
        name: "Ostendo item search",
        table: "Ostendo item master",
        source: "Ostendo live search",
      },
    ],
    calculations: [
      {
        metric: "Panel order",
        formula: "Active rows use display order in tens so a future test can be inserted between two rows",
      },
      {
        metric: "Versioned limits",
        formula: "A new dated panel row becomes effective while the previous row is retained for historical result checks",
      },
      {
        metric: "Sheet header",
        formula: "An ordered list of fields from a fixed set, each with an optional label; a blank label uses the default, and a material with no list shows the fields its samples carry",
      },
    ],
    limitations: [
      "Materials must be selected from Ostendo; this page does not create or rename Ostendo items.",
      "There are no delete controls. Deactivation preserves the panel history.",
      "A test added back after deactivation starts with no section. Set it again on its row.",
    ],
    version: "v1.1",
    lastUpdated: "2026-09-28",
  },

  "/rm-materials": {
    title: "Raw Material Records",
    description:
      "Each raw material's own page, in the old LIMS layout: every sample as a row, the material's header fields first in its own wording, then its tests under their section headings, with a chart of one numeric test when you want it. This index lists the materials; open one to see its records. Read only: results are entered and corrected on the bench sheet.",
    dataSources: [
      {
        name: "Raw-material samples and results",
        table: "rm_sample, rm_result",
        source: "Live production records",
      },
      {
        name: "Material layout and panel",
        table: "rm_material (header fields), rm_material_test (tests, sections, limits)",
        source: "Live production records",
      },
    ],
    calculations: [
      {
        metric: "Column order",
        formula:
          "Header fields in the material's own order and labels, then tests without a section, then each named section in the order of its first test. A material with no header list shows the sample date and the identity fields its samples carry",
      },
      {
        metric: "Cell shading",
        formula: "Amber outside the warning limits and red outside the error limits in force on the sample's date, the same check as the bench sheet",
      },
      {
        metric: "Chart",
        formula:
          "One point per sample holding a number for the chosen test, over sample date. Crosbe tests and supplier tests are separate lines; dashed lines are today's panel limits",
      },
    ],
    limitations: [
      "The newest 100 samples load first; the button under the table loads older ones, and sorting applies to the samples loaded.",
      "A result whose test has since left the panel still shows, in a column at the end under its own heading.",
      "The chart draws today's limits, not the limits that applied on each sample's date.",
      "The CoA column shows the recorded filename when present, “Attached” when the attachment is recorded without a filename, “Not received” when that status is recorded, and otherwise stays blank. This page cannot attach certificates.",
    ],
    version: "v1.0",
    lastUpdated: "2026-09-29",
  },

  "/rm-categories": {
    title: "Raw Materials",
    description:
      "Every active raw material as a card, grouped under QC's categories in QC's order, the same layout as the Finished Goods control charts. Each card shows the material's name, its Ostendo code and how many samples it holds; open a card to go to that material's own records page. Read only.",
    dataSources: [
      {
        name: "Raw-material catalogue and sample counts",
        table: "rm_material, rm_sample",
        source: "Live production records",
      },
    ],
    calculations: [
      {
        metric: "Grouping",
        formula:
          "Each material sits under the category set on it in the Field Manager. Categories follow QC's order; a category with no material is not shown; materials with no category are listed last under Uncategorised",
      },
      {
        metric: "Card order",
        formula: "Alphabetical by material name within a category, as on the Finished Goods cards",
      },
      {
        metric: "Samples",
        formula: "Active test samples for the material; sieve runs are counted separately and shown when there are any",
      },
    ],
    limitations: [
      "Inactive materials are not shown.",
      "A category is changed in the Field Manager, not here.",
    ],
    version: "v1.0",
    lastUpdated: "2026-09-29",
  },

  "/raw-materials?tab=records": {
    title: "Raw Material Records",
    description:
      "Raw materials laid out like Finished Goods' Records view. With no material chosen, the materials show as cards under their QC categories (Uncategorised last), each with its sample count. Choose one to see every sample in a date range as a row and each test on its panel as a column, checked against spec limits. The chart button on a numeric column opens that test's control chart over the table.",
    dataSources: [
      { name: "Raw material samples & results", table: "rm_sample, rm_result", source: "Live production records, read only here" },
      { name: "Test panels & spec limits", table: "rm_test_definition, rm_material_test", source: "Per-material panels and today's limits" },
    ],
    calculations: [
      { metric: "Result check", formula: "Outside the error limits = fail, outside the warning limits = warn, inside = pass, no limits set = no check" },
      { metric: "Control chart", formula: "Crosbe's own results for the test in the date range, oldest first: mean, UCL/LCL = mean ± 3σ and Nelson rules, the same arithmetic as the Finished Goods chart, against today's panel limits" },
      { metric: "Chart source", formula: "Crosbe (the default) charts Crosbe's results; Supplier charts the supplier-declared values; Both charts the two as separate series. Mean, UCL, LCL and Nelson alerts always come from Crosbe's results only" },
    ],
    limitations: [
      "Supplier-declared values never enter the chart's statistics. The source choice shows only when the date range holds supplier values for that test.",
      "The chart draws today's limits for the whole range; older results were checked against the limits of their day.",
      "The table loads 100 samples at a time and the chart up to 1,000; both say when there are more.",
      "Read only. Results are entered on the Bench Sheet tab.",
    ],
    version: "v1.1",
    lastUpdated: "2026-10-14",
  },

  "/raw-materials?tab=test-entry": {
    title: "Raw Material Bench Sheet",
    description:
      "The QC workbook, live: pick a material and see every sample as a row against its test panel as columns, the same grouped headers as the Excel sheet, with each cell checked against spec limits. The top row is the entry row: type results across it like a spreadsheet and save; the supplier's declared certificate values sit pinned beneath it for eyeball comparison.",
    dataSources: [
      { name: "Raw material samples & results", table: "rm_sample, rm_result", source: "Live production records, every saved entry permanent and audited" },
      { name: "Test panels & spec limits", table: "rm_test_definition, rm_material_test", source: "Canonical test registry + per-material panels (limits are versioned; new limits never re-judge old results silently)" },
    ],
    calculations: [
      { metric: "Result check", formula: "Outside the error limits = fail, outside the warning limits = warn, inside = pass, no limits set = no check" },
      { metric: "Premix reference", formula: "Materials that require a bundled anhydrite premix (Buzzi, AliPre, Istra 40) are tested with it recorded. The premix reference identifies the test condition, so a missing value shows a warning; the save is never refused" },
      { metric: "Header and sections", formula: "A material with a header list in the Field Manager shows exactly those fields, in that order, with their labels. Its tests show the main block first, then each named section under its own heading; the same test can appear in both, saved separately" },
    ],
    limitations: [
      "Most panels carry no approved spec limits yet, so those results show 'no limits' rather than a pass/fail.",
      "Recorded results cannot be edited. A correction is a new entry, because the record is append-only by design.",
      "The sheet shows the latest 100 samples for the selected material; when there are more, the page says so.",
      "The CoA column shows a dash on every row: a CoA cannot be attached to a sample from this sheet yet.",
    ],
    version: "v3.2",
    lastUpdated: "2026-09-28",
  },

  "/raw-materials?tab=sieve": {
    title: "Sieve Analysis",
    description:
      "Enter particle-size runs down the fixed 17-sieve ladder and compare any set of runs side by side, across dates, silos and suppliers, with an overlaid cumulative passing curve.",
    dataSources: [
      { name: "Sieve runs", table: "rm_sample, rm_sieve_result", source: "Live production records: raw retained grams per sieve, stored permanently" },
    ],
    calculations: [
      { metric: "% retained", formula: "retained g ÷ total g × 100 (total includes the pan), always derived from the raw grams, never stored" },
      { metric: "Cumulative % passing", formula: "100 − sum of % retained on this sieve and every coarser sieve" },
    ],
    limitations: [
      "Comparison is capped at 12 runs. More curves than that are unreadable on one chart.",
    ],
    version: "v3.0",
    lastUpdated: "2026-08-11",
  },

  "/raw-materials?tab=day": {
    title: "RM QC Day Sheet",
    description:
      "The raw-material QC timeline: every test entry and sieve run across all materials, in one stream, newest first, grouped by day. Filter to flagged to see only what needs a second look.",
    dataSources: [
      { name: "Samples, results & sieve runs", table: "rm_sample, rm_result, rm_sieve_result", source: "Live production records" },
    ],
    calculations: [
      { metric: "Day grouping", formula: "Entries group under their Sydney calendar day" },
    ],
    limitations: [
      "Read-only. Recording happens on the Bench Sheet and Sieve Analysis tabs.",
      "The stream shows the latest 100 test entries and the latest 100 sieve runs; when there are more, the page says so. One material at a time on the Bench Sheet reaches further back.",
      "Flagged covers warns, fails and unit mismatches. Sieve runs have no pass/fail check, so they only appear under Everything.",
    ],
    version: "v1.1",
    lastUpdated: "2026-08-12",
  },

  "/raw-materials?tab=due": {
    title: "RM QC Due",
    description:
      "Aged tests waiting to be taken. Logging a sample creates future work, because 24-hour and 7-day strengths mature later. This tab lists every maturing test with its due time, overdue first. Entering the result on the Bench Sheet clears the row automatically.",
    dataSources: [
      { name: "Samples & panels", table: "rm_sample, rm_result, rm_material_test, rm_test_definition", source: "Live production records" },
    ],
    calculations: [
      { metric: "Due time", formula: "Sample date + the test's age (24 h, 7 d…), for every aged panel test without a recorded result" },
    ],
    limitations: [
      "Derived live from what is recorded, so a test recorded under a different sample of the same lot still shows as due on the original sample.",
    ],
    version: "v1.0",
    lastUpdated: "2026-08-11",
  },

  "/raw-materials?tab=receipts": {
    title: "Receipts",
    description:
      "Incoming raw-material deliveries: receipt number, date, supplier, item code, description, quantity, unit and PO number. Pricing is never shown.",
    dataSources: [
      { name: "Purchase receipts", table: "PURCHASERECEIPTS, PURCHASERECEIPTLINES", source: "Ostendo ERP (Coopers), live" },
    ],
    calculations: [],
    limitations: [
      "Up to 200 lines per view, newest first. Narrow the dates to see older lines in a busy range.",
      "Fuel surcharge and freight charge lines are not shown: they are charges, not material.",
      "One year at most per search.",
      "Only roles granted receipts access see this tab.",
    ],
    version: "v1.0",
    lastUpdated: "2026-09-29",
  },

  "/raw-materials?tab=coa-check": {
    title: "COA Cross-Check",
    description:
      "For a delivery lot: our measured results beside the supplier's declared values. Shared tests appear first, then values only the supplier declares, then values only we measure.",
    dataSources: [
      { name: "Our test results", table: "rm_sample, rm_result", source: "Live production records" },
      { name: "Supplier declared values", source: "Supplier document registry (synced certificate values per material)" },
    ],
    calculations: [
      { metric: "Result check", formula: "Our result against our spec limits. The supplier's declared figure sits alongside for reference and is not judged" },
      { metric: "Comparability", formula: "Each test on a material's panel has its own setting for whether our result is comparable to the supplier's figure. A shared test set as not comparable (for example a result we measure with our own anhydrite premix while the supplier doses its own) is labelled 'not comparable' instead of being read against the supplier's figure" },
    ],
    limitations: [
      "Declared values older than 48 hours since their last sync show a warning, because an out-of-date supplier document can silently mislead.",
      "Units are never converted: a declared value in an unrecognised unit is shown exactly as recorded and flagged.",
      "Supplier names that have not been mapped to our canonical test names yet appear under 'declared only' rather than beside our result.",
    ],
    version: "v3.0",
    lastUpdated: "2026-08-11",
  },

  "/analyst": {
    title: "AI Data Analyst",
    description:
      "Conversational AI interface with 14 tools for querying production, quality, cost, and carbon data. Creates inline charts and can generate interactive artifacts.",
    dataSources: [
      { name: "All postgres tables", source: "Cloud SQL via run_sql tool (read-only, max 500 rows)" },
      { name: "Cost data", table: "raw_material_costs, material_cost_map (D1)", source: "D1 via get_material_costs tool" },
      { name: "Carbon data", table: "emission_factors (D1)", source: "D1 via calculate_carbon tool" },
      { name: "Knowledge base", table: "kb_articles + kb_articles_fts (D1)", source: "D1 via search_kb tool" },
      { name: "Product catalogue", table: "products + products_fts (D1)", source: "D1 via get_product_info tool" },
    ],
    calculations: [
      { metric: "Tool calling", formula: "AI decides which tools to use, executes them, analyses results, and responds. Up to 25 rounds per conversation." },
      { metric: "Models", formula: "Automatic (the default) answers on Claude Opus. Claude Sonnet (balanced) and Claude Haiku (fast) are user-selectable." },
    ],
    limitations: [
      "AI responses may vary between runs. They are not deterministic.",
      "SQL queries are read-only (validated by validateQuery before execution).",
      "Max 500 rows returned per query. Complex joins may timeout.",
      "Artifact generation runs in the background and may take 15-30 seconds.",
    ],
    version: "v2.0",
    lastUpdated: "2026-03-28",
  },

  "/digests": {
    title: "Plant Digests",
    description:
      "AI-generated plant operations summaries. Choose a period (daily/weekly/monthly/yearly) and the AI writes a narrative analysis of production, materials, and lab data. The bar under the periods says whether today's morning digest email went out; if it did not, an admin can send it from 07:30.",
    dataSources: [
      { name: "Production + materials + lab metrics", source: "Cloud SQL, parallel queries for each data domain" },
      { name: "AI narrative", source: "Claude Sonnet writes the digest text" },
      { name: "Today's morning digest", source: "The once-a-day send record shared by the 07:00 run and the admin send" },
    ],
    calculations: [
      { metric: "Period logic", formula: "Daily = yesterday, Weekly = last Mon-Sun, Monthly = previous calendar month, Yearly = previous Australian FY (Jul-Jun)" },
    ],
    limitations: [
      "AI-generated text may vary between runs.",
      "Monthly = previous calendar month (not rolling 30 days).",
      "Yearly = Australian FY (Jul 1 - Jun 30), not calendar year.",
      "The morning digest emails once a day: after it has gone out, from the 07:00 run or an admin's send, it cannot be sent again that day.",
    ],
    version: "v2.1",
    lastUpdated: "2026-10-23",
  },

  "/display": {
    title: "Display Mode",
    description:
      "Full-screen auto-cycling dashboard for factory floor TVs or boardroom projectors. Shows KPIs, cost breakdown, shift patterns, and daily production.",
    dataSources: [
      { name: "Same as Overview + Cost Analysis + Shift Analysis", source: "Cloud SQL + D1" },
    ],
    calculations: [
      { metric: "Auto-cycle", formula: "6 slides, 12-second interval, pause/play controls" },
    ],
    limitations: [
      "Requires authentication: you must be logged in to access it.",
      "Figures refresh every 5 minutes, so they are not truly real-time.",
      "When the figures stop refreshing, the header shows the time they were last updated in place of Live.",
      "If the sign-in lapses, the screen keeps showing its last figures, and a new version waits until the sign-in is back.",
    ],
    version: "v2.0",
    lastUpdated: "2026-10-28",
  },

  "/qa-entry": {
    title: "QA Entry",
    description:
      "Enter and correct QC samples. Each entry writes straight to the live QC tables and appears on the QC charts, Cpk and the testing calendar. Columns follow the product; spec limits colour values as you type.",
    dataSources: [
      { name: "Samples", table: "qa_production_*sample (live, written by this page)", source: "Cloud SQL" },
      { name: "QA columns", table: "information_schema.columns for qa_production_*sample", source: "Cloud SQL" },
      { name: "Spec limits", table: "qa_production_*config", source: "Cloud SQL" },
    ],
    calculations: [
      { metric: "Spec feedback", formula: "Real-time colour coding as values are entered: green (in spec), amber (warning), red (error)" },
      { metric: "Test Date", formula: "Blank = the sample date", notes: "Set it only when the tests were run on a different day; it drives the testing calendar." },
    ],
    limitations: [
      "Columns vary by product.",
    ],
    version: "v2.1",
    lastUpdated: "2026-09-03",
  },

  "/spec-limits": {
    title: "Specifications",
    description:
      "Per-product QA configuration: tick which test-type sets a product actually runs (compressions, flexurals, shrinkages, flow, setting time, …), edit the 4-tier spec-limit bands (Error Low / Warn Low / Warn High / Error High) per test, and maintain the per-product bag weights used to convert AO tail bag counts into tonnage. Absorbed the retired standalone Test Types page (old /test-types links redirect here).",
    dataSources: [
      {
        name: "Test columns + current spec bands",
        table: "information_schema.columns + qa_production_{slug}config",
        source: "Cloud SQL (live)",
        refresh: "Live per product selection",
      },
      {
        name: "Test-type active flags",
        table: "qa_test_type_flags (D1)",
        source: "Crosbe AI D1 database",
      },
      {
        name: "Bag weights",
        table: "qa_production_bagkg (legacy BagKg admin table)",
        source: "Cloud SQL (live), hardcoded seed as fallback",
      },
      {
        name: "Testing frequency (Part Test / BOD batch numbers)",
        table: "qa_testing_frequency (D1)",
        source: "Crosbe AI D1 database",
      },
    ],
    calculations: [
      {
        metric: "Effective test-type visibility",
        formula: "set flag AND per-test flag, so a test appears on QA Entry and the testing calendar only when its whole set is ticked and the individual test isn't switched off",
        notes: "Sets are ticked as a whole (e.g. all compression ages together), per Gus's workflow.",
      },
      {
        metric: "Spec band roles",
        formula: "Error Low < Warn Low < value < Warn High < Error High",
        notes: "Error bands drive Cpk and out-of-spec flags; warning bands drive amber highlighting on QA Entry and the QC charts.",
      },
      {
        metric: "Bag weight → tonnage",
        formula: "AO tail bag count × bag_weight_kg ÷ 1000",
        notes: "Feeds the QC Stats KPIs. Rows without a live DB value fall back to a built-in default (marked as fallback).",
      },
      {
        metric: "Testing frequency",
        formula: "Batch numbers in a production run at which a part test and a bag-of-day sample are taken, e.g. 5;20;35;55",
        notes: "Set per product. Every change is recorded with who made it, when, the previous and new values, and any reason given.",
      },
    ],
    limitations: [
      "Editing is role-gated: spec bands and bag weights need the edit_spec_config permission, test-type flags need edit_test_types (QC Manager by default).",
      "Spec-limit writes only update band values on a product's existing test columns. Adding, removing or reordering tests is not possible here.",
      "Replicate pairs (_a/_b) share one set of bands, so saving either writes the same config base (last write wins).",
      "Test-type flags live in the app's D1 database, not the legacy Django config tables, so the legacy app doesn't see them.",
    ],
    version: "v1.0",
    lastUpdated: "2026-07-04",
  },

  "/rd/mix-designs": {
    title: "R&D Mix Designs",
    description:
      "Research mix design management with 65+ test columns, ingredient editing, auto-numbering, and duplicate functionality. Creating and editing a design writes to the live research tables. A new product family can be started from New family beside Product on a new design; its code, up to 5 capital letters or digits, starts every mix number in that family and cannot be changed afterwards. Saved designs accept photos and videos with notes; videos can also be labelled with the test they show. Ledger shows one product's designs as a single test sheet; Timeline shows the same designs as a day-by-day stream of what was recorded. Both show the reported figures, the same numbers the lab quotes, rather than the values as they were typed in; the Ledger can switch to 'As entered'.",
    dataSources: [
      { name: "Mix designs", table: "research_mixdesign (2,028 rows)", source: "Cloud SQL" },
      { name: "Ingredients", table: "research_ingredient (16,087 rows)", source: "Cloud SQL" },
      { name: "Products + materials", table: "research_product, research_rawmaterial", source: "Cloud SQL" },
      { name: "Reference values", table: "research_referencevalue (per design)", source: "Cloud SQL" },
    ],
    calculations: [
      { metric: "Mix design number", formula: "{product.code}-{sequential_count} with uniqueness check" },
      {
        metric: "Bleed (%)",
        formula: "bleed grams ÷ the design's reference bleed × 100, to 2 decimal places",
      },
      {
        metric: "Expansion (%)",
        formula: "expansion mm ÷ the design's reference expansion × 100, to 2 decimal places",
      },
      {
        metric: "Shrinkage ΔL (µs)",
        formula:
          "(age reading − 24-hour reading) ÷ (reference shrinkage + 24-hour reading) × 1,000,000, to the nearest microstrain",
        notes:
          "Dry and wet are measured against their own 24-hour reading, which is shown as the baseline rather than as a result.",
      },
      {
        metric: "Compressive strength per age",
        formula: "mean of the two cubes (A and B); A alone when only A was broken",
      },
      {
        metric: "Ledger difference column",
        formula: "figure − the same figure on the pinned design",
        notes: "Shown while a design is pinned. Mix designs have no spec limits, so a difference is never a pass or fail.",
      },
      {
        metric: "Timeline day",
        formula: "Sample date and time in Sydney time",
        notes: "Designs with no sample date are grouped at the end rather than dropped.",
      },
      {
        metric: "Normalise",
        formula:
          "each entered % × target ÷ current total, rounded to 4 decimal places so the rows land on the target exactly; the last rounding step goes to the row with the largest remainder (ties to the earlier row)",
        notes:
          "A row with more than 4 decimal places is left out of the Total but not out of Normalise: the factor shown and Apply both include it, and Apply rounds it to 4 decimal places with the rest.",
      },
    ],
    limitations: [
      "65 test columns, not all visible at once; scroll horizontally.",
      "Photos are limited to 10 MB each. Videos accept MP4, MOV or WebM up to 60 MB each; playback depends on the recording format your browser supports, and the original can be downloaded.",
      "Ledger hides test columns that no design in the current scope has recorded; switch to the full test sheet to see them all.",
      "Ledger, Timeline and Results show up to 200 designs in the current scope, the first 200 in the order the sort sets. Search, or narrow by product, status or raw material, to reach the rest.",
      "Flow and efflux readings are reported exactly as measured, with no calculation applied.",
      "A design with no reference value on record shows its bleed, expansion and shrinkage as entered, marked with a small circle.",
      "Timeline opens on designs that have results; switch to Everything to include designs with none recorded yet.",
    ],
    version: "v2.5",
    lastUpdated: "2026-10-06",
  },

  "/rd/pipeline": {
    title: "R&D Pipeline",
    description:
      "Tracks research products from mix-design through to production: per-product design counts (by status), whether the product has reached production, and how many batches it has run. KPIs summarise the R&D→Production conversion across all researched products.",
    dataSources: [
      {
        name: "Research products + designs",
        table: "research_product LEFT JOIN research_mixdesign",
        source: "Cloud SQL",
        refresh: "Live per page load",
      },
      {
        name: "Production batches",
        table: "production_monitor_batch (grouped by product_lookup)",
        source: "Cloud SQL",
        refresh: "Live per page load",
      },
    ],
    calculations: [
      {
        metric: "Designs / Active / Draft / Archived",
        formula:
          "COUNT(research_mixdesign) per research_product, FILTERed by status ('active' / 'draft' / 'archived')",
      },
      {
        metric: "In Production",
        formula:
          "research product has a fuzzy name-match against a production_monitor_batch product_lookup (findProductionMatch)",
        notes: "Matching is by product NAME, not a hard key. See limitations.",
      },
      {
        metric: "Conversion Rate",
        formula: "round(products_in_production / products_with_designs × 100)",
        notes: "Denominator is research products with ≥1 design, not all research_product rows.",
      },
      {
        metric: "Total Production (per product)",
        formula:
          "SUM(production_monitor_batch.weight_kg) for the matched product, shown in tonnes (kg ÷ 1000)",
      },
    ],
    limitations: [
      "Production matching is FUZZY name comparison, so a renamed or differently-coded product can mismatch or miss, under- or over-counting 'In Production'.",
      "Some R&D products (experimental formulations, trial blends) are never intended for production, so a low conversion rate isn't necessarily a problem.",
      "Products with zero mix designs are hidden from the 'All' filter.",
    ],
    version: "v1.0",
    lastUpdated: "2026-06-17",
  },

  "/materials?tab=purchase-orders": {
    title: "Purchase Orders",
    description:
      "Inbound materials view across three tabs: recent goods Receipts, Open POs (awaiting delivery), and QC-Pending batches (received material not yet quality-tested). KPIs summarise receipt count/value, open-PO count, and QC backlog. Critical raw materials (OPC, CSA, CAC, Anhydrite, etc.) are highlighted.",
    dataSources: [
      {
        name: "Goods receipts",
        table: "PurchaseReceipts + PurchaseReceiptLines",
        source: "Ostendo ERP",
        refresh: "Cached 1 hour",
      },
      {
        name: "Purchase orders",
        table: "PurchaseHeader + PurchaseLines",
        source: "Ostendo ERP",
        refresh: "Cached 1 hour",
      },
      {
        name: "Batches + item descriptions",
        table: "ItemBatches + ItemMaster",
        source: "Ostendo ERP",
        refresh: "Cached 1 hour (ItemMaster cached longer)",
      },
    ],
    calculations: [
      {
        metric: "Recent Receipts / Receipt Value",
        formula: "PurchaseReceipts within the last 60 days; value = sum of line totals over that window",
      },
      {
        metric: "Open POs",
        formula: "PurchaseHeader rows whose ORDERSTATUS is neither 'closed' nor 'cancelled'",
      },
      {
        metric: "QC Pending",
        formula: "ItemBatches where BATCHSTATUS = 'QC Required', excluding zero-qty-on-hand batches",
        notes: "Zero-qty batches are treated as stale QC records (material already consumed) and filtered out.",
      },
      {
        metric: "Critical-material highlight",
        formula: "Keyword match on description against OPC / CSA / ANHYDRITE / CAC / GP CEMENT / GREY CEMENT / ICL",
      },
    ],
    limitations: [
      "Sourced from Ostendo ERP and cached for 1 hour, not real-time.",
      "Receipts tab is a rolling 60-day window, not full history.",
      "QC-pending count excludes zero-on-hand batches, so it reflects testable stock, not every 'QC Required' record.",
      "Critical-material highlighting is keyword-based on the description text, so a non-standard description can miss.",
    ],
    version: "v1.0",
    lastUpdated: "2026-06-17",
  },

  "/calibration": {
    title: "Calibration Intelligence",
    description:
      "Lab equipment calibration tracker. Shows calibration status (OK / due soon / overdue) against an annual cycle, average calibration interval, and a value-drift trend per instrument.",
    dataSources: [
      {
        name: "Equipment + calibration history",
        table: "crosbe_equipment LEFT JOIN crosbe_equipmentcalibration",
        source: "Cloud SQL (live)",
        refresh: "Live per page load",
      },
    ],
    calculations: [
      {
        metric: "Status",
        formula:
          "next_due = last_calibrated + 1 year; overdue if days_until_due < 0, due_soon if < 30, else ok, unknown if never calibrated",
        notes: "Fixed 365-day annual cycle, not read from a per-instrument schedule.",
      },
      {
        metric: "Average Interval",
        formula: "(last_calibration_date - first_calibration_date) / (calibration_count - 1), in days",
        notes: "Needs at least 2 calibrations.",
      },
      {
        metric: "Drift",
        formula:
          "Linear-regression slope of numeric calibration value over reading index; rising if slope > 1% of mean, falling if < -1% of mean, else stable",
        notes: "Needs at least 3 numeric readings; non-numeric values give insufficient_data.",
      },
      {
        metric: "Compliance Rate",
        formula: "round(ok_count / total_equipment × 100)",
        notes: "Only 'ok' counts as compliant; due_soon/overdue/unknown do not.",
      },
    ],
    limitations: [
      "Annual cycle is hardcoded at 365 days from last calibration, not driven by each instrument's actual NATA schedule.",
      "Drift uses simple linear regression on reading order (not time-weighted) and needs 3+ numeric readings.",
      "Equipment with non-numeric calibration values shows no trend chart and no drift classification.",
      "People with the edit_equipment permission can add calibration records and manage equipment from this page; everyone else can view the data only.",
    ],
    version: "v1.0",
    lastUpdated: "2026-06-17",
  },

  "/materials?tab=inventory": {
    title: "Inventory Levels",
    description:
      "A sortable view of raw-material stock: what is on hand, what is on order and when it is due, how much is used each day, and how many days the stock covers at that use. Stock and QC pending load first; the order, use and cover figures follow a moment later.",
    dataSources: [
      {
        name: "Stock batches",
        table: "ItemBatches",
        source: "Ostendo ERP (COOP)",
        refresh: "Cached 1 hour",
      },
      {
        name: "Item descriptions + categories",
        table: "ItemMaster",
        source: "Ostendo ERP (COOP)",
        refresh: "Cached 24 hours",
      },
      {
        name: "On hand, on order, usage and safety stock",
        source: "The same figures RM Ordering uses (Ostendo stock, open purchase orders, last 3 months of use, planner settings)",
        refresh: "Supply figures refresh every 15 minutes",
      },
    ],
    calculations: [
      {
        metric: "On hand (per material)",
        formula: "Stock on hand in tonnes, the same figure RM Ordering shows once supply figures have loaded",
        notes: "Until supply figures load (or if they cannot be loaded), On hand is the batch total from Ostendo and the filters are off. Items whose unit is not a weight show their own unit and are left out of the cover figures.",
      },
      {
        metric: "On order (per material)",
        formula: "Every open purchase order for the material, with the next due date",
        notes: "RM Ordering counts only orders inside its planning window, so this can be higher. An order past its due date is shown as late.",
      },
      {
        metric: "Used per day",
        formula: "Average daily use over the last 3 months, with an arrow for rising, falling or steady",
        notes: "Shows 'Usage unavailable' when the use history cannot be read.",
      },
      {
        metric: "Days of cover at recent use",
        formula: "(on hand + on order) ÷ used per day",
        notes: "Shows 'No recent use' when nothing was used in the last 3 months.",
      },
      {
        metric: "QC Pending (per material)",
        formula: "COUNT of the material's batches where BATCHSTATUS = 'QC Required'",
      },
      {
        metric: "Cards and filters",
        formula: "Below safety stock, under 14 days of cover, late orders, no recent use: the same four counts as the filter buttons",
        notes: "The cards count every material, whatever the search or category.",
      },
    ],
    limitations: [
      "Sourced from Ostendo ERP and cached, not real-time. If the supply figures cannot load, stock and QC pending still show.",
      "Items with only zero/negative on-hand batches do not appear unless they are raw materials in the plan.",
      "Category and description come from ItemMaster; items missing an ItemMaster row show the bare item code.",
    ],
    version: "v2.0",
    lastUpdated: "2026-11-02",
  },

  "/insights": {
    title: "Insights",
    description:
      "Aggregated quality alerts across all QA products for the results that came due in the selected date range. Scans each product's Cpk test (28-day compressive strength unless the product's Cpk source names another) for Nelson Rule anomalies, computes Cpk, and counts at-risk strength predictions.",
    dataSources: [
      {
        name: "QA sample data (per product)",
        table: "qa_production_{slug}sample (all QA_PRODUCTS tables)",
        source: "Cloud SQL (live)",
        refresh: "Live per page load",
      },
      {
        name: "Spec limits (per product)",
        table: "qa_production_{slug}config (*_lower_limit_error / *_upper_limit_error)",
        source: "Cloud SQL (live)",
      },
    ],
    calculations: [
      {
        metric: "Nelson Alerts (Critical / Warning)",
        formula:
          "computeNelsonRules over the product's Cpk-test results (28d compressive strength by default); critical = severity 'critical', warning = severity 'warning'",
        notes: "Requires at least 3 valid samples per product, else the product is skipped.",
      },
      {
        metric: "Cpk",
        formula: "MIN((USL - mean)/3σ, (mean - LSL)/3σ) using the product's error spec limits; σ is sample stddev (N-1)",
        notes: "Computed only when stddev > 0 and at least one spec limit exists.",
      },
      {
        metric: "At Risk",
        formula:
          "Count of samples whose predicted 28d strength (regressed from 24h/3d/7d early-age data) falls outside spec, where the regression r-squared > 0.3",
      },
      {
        metric: "Cpk Rating",
        formula: ">=1.67 Excellent, >=1.33 Capable, >=1.0 Marginal, <1.0 Incapable",
      },
    ],
    limitations: [
      "Assesses one test per product, its Cpk source: 28-day compressive strength unless the product's Cpk source names another.",
      "The date range is when each result came due: the sample date plus the test's age, so a 28-day result counts 28 days after its sample. At-risk predictions cover samples taken in the range whose result is not in yet.",
      "Products with fewer than 3 valid samples in the range are skipped entirely.",
      "At-risk predictions only fire when an early-age regression model clears r-squared > 0.3; weak correlations produce no predictions.",
      "Default range is the last 90 days; changing dates re-scans every product on each request (no server-side cache on this endpoint).",
    ],
    version: "v1.1",
    lastUpdated: "2026-10-27",
  },

  "/schedule": {
    title: "Schedule",
    description:
      "Production and dispatch timeline board, the native replacement for the external ScheduleIt board. Groups (Production, sites, CS & Dispatch, Transport) hold resource lanes; bookings render as blocks across a day timeline. Copied from ScheduleIt on a schedule an admin sets (once a day at 1am Sydney time, 3-hourly, or every hour); it is not live, and the last copy time is shown at the top of the page. Bookings can also be created, edited and deleted here.",
    dataSources: [
      {
        name: "Board data (groups, lanes, bookings)",
        table: "sched_groups / sched_resources / sched_events (+ join tables)",
        source: "App D1 (mirrored from ScheduleIt)",
        refresh: "On the schedule an admin sets (once a day at 1am Sydney time, 3-hourly, or every hour); can also be synced on demand; the last copy time is shown at the top of the page",
      },
    ],
    calculations: [
      {
        metric: "Parsed booking fields",
        formula:
          "Booking titles keep the dispatcher grammar (run order, ** time window **, customer, suburb, unload method, qty, DD docket); the importer extracts these best-effort into filterable fields without altering the title.",
      },
    ],
    limitations: [
      "The scheduled sync overwrites edits to bookings that came from ScheduleIt; bookings created here persist.",
      "Synced on the schedule an admin sets, not live. ScheduleIt remains the operational source of truth until cutover.",
      "Field extraction from titles is best-effort; the title text is always authoritative.",
    ],
    version: "v0.3",
    lastUpdated: "2026-09-28",
  },

  "/rm-planner": {
    title: "RM Ordering",
    description:
      "Predictive raw-material purchasing. Reads the planned production runs from the Schedule, works out how much of each raw material they consume, subtracts what's already in stock, already on order and already booked as a bulk delivery on the Schedule board, and suggests what to buy and by when. Most-urgent materials sort to the top.",
    dataSources: [
      {
        name: "Production plan (demand)",
        table: "Schedule board (Production runs)",
        source: "App D1 (mirrored Schedule)",
        refresh: "Follows the Schedule sync cadence an admin sets",
      },
      {
        name: "Bulk deliveries booked on the Schedule board",
        table: "Schedule board (Bulk Raw Del lane)",
        source: "App D1 (mirrored Schedule)",
        refresh: "Follows the Schedule sync cadence an admin sets",
      },
      {
        name: "Stock on hand, open purchase orders, recipes (BOMs)",
        table: "Inventory, purchase orders, bills of materials",
        source: "Ostendo (manufacturing)",
        refresh: "Daily snapshot",
      },
      {
        name: "Planner settings (lead times, safety stock, criticality, run→product mapping)",
        table: "rm_planner_config / rm_material_policy / rm_product_map",
        source: "App D1 (edited in Settings)",
        refresh: "On save",
      },
    ],
    calculations: [
      {
        metric: "Net to order",
        formula:
          "For each week: raw-material demand from the plan (recipe × planned tonnes) minus projected stock on hand, where stock is rolled forward with incoming purchase orders. A shortfall below the safety-stock level is the net requirement.",
      },
      {
        metric: "Suggested order & order-by date",
        formula:
          "The quantity that keeps stock at or above safety across the horizon (rounded up to a full load where set), dated to be ordered its supplier lead time before stock would run low.",
      },
    ],
    limitations: [
      "Stock, purchase-order and recipe data is a daily snapshot, so figures are as of the last refresh, not live to the minute.",
      "Needs its settings seeded: production-run → product mappings and per-material lead time / safety stock. Until a material is set up it uses safe defaults and flags them.",
      "Lead times supplied by the manufacturing system are unreliable for imported materials. Some read as zero days, which would date an order after the stock runs out. Materials with an overridden lead time say so on the row; anything not yet overridden still plans on the supplied figure.",
      "Production runs that can't be read or mapped to a product are listed in an 'unmapped' bucket and excluded from demand rather than guessed.",
    ],
    version: "v0.2",
    lastUpdated: "2026-08-21",
  },

  "/product-inventory": {
    title: "Product Inventory",
    description:
      "Finished-goods stock for the morning production stand-up, in pallets: stock on hand at each site, what QC is holding back, what is moving between sites, what Coopers has finished, what is on open orders, and what is booked to run today. Every figure is read when the page loads, and a figure that could not be read shows as ? with the reason on hover, never as zero. Distinct from the Materials Inventory tab, which is raw material.",
    dataSources: [
      {
        name: "Site stock and Coopers Incoming",
        table: "VW_CAI_STOCK_ON_HAND: Approved stock at Seven Hills, Laverton Nth, QLD-O'Connors and Coopers Finished Goods, plus stock lines with no QC status entry at the three Crosbe National sites",
        source: "Ostendo live, both companies; the overnight copy when the live read is unavailable, labelled daily copy",
        refresh: `Read on page load and held for 10 minutes. ${PRODUCT_INVENTORY_COST}. The Site stock pill shows the source, the read time and whether the read was cut short`,
      },
      {
        name: "QC release (held stock)",
        table: "qa_production_upi, joined to Ostendo batches by AO number",
        source: "Plant database, read directly, because Ostendo's batch status is keyed by hand afterwards and trails it",
        refresh: "Read on page load; its own QC release pill",
      },
      {
        name: "On Order",
        table: "SALESHEADER + SALESLINES, open orders, quantity left to ship",
        source: "Ostendo (Crosbe National), the same source decision as site stock",
        refresh: "Read on page load",
      },
      {
        name: "In-Transfer",
        table: "INVENTORYTRANSFERS + INVENTORYTRANSLINES, open transfers",
        source: "Ostendo, both companies",
        refresh: "Read on page load",
      },
      {
        name: "Production today and Notes",
        table: "sched_events, the app's copy of the ScheduleIt board",
        source: "ScheduleIt, synced into the app on the schedule set by the sync cadence",
        refresh: "Read on page load; the Schedule pill shows the last successful sync and turns amber once it is older than one sync interval plus two hours",
      },
      {
        name: "Bags per pallet",
        table: "ITEMMASTER (ITEMDESCRIPTION pallet config, ADDITIONALFIELD_2 as a cross-check)",
        source: "Ostendo daily copy, Crosbe National (sales) company only",
        refresh: "One statement per load, never live; the page holds the result for 10 minutes",
      },
    ],
    calculations: [
      {
        metric: "Pallets",
        formula:
          "Each product's quantity divided by that product's own bags-per-pallet ratio. Authority, in order: a ratio recorded in the app for that product (one product today, IG 110 A+ LC); then the pallet configuration in the product's Ostendo item description, for example (70), as Crosbe National's item master holds it on the daily copy. The item master's pallet field is read beside it as a cross-check only. Bulk binders stay in tonnes, because a 1 t bulk bag is one pallet, and primers and packaging stay in units. No default ratio is ever used.",
        notes: "Hover any figure for its bag count and the ratio behind it. ~ marks a ratio that is assumed rather than measured; a + on a total means one product in it has no ratio, so the total is a floor.",
      },
      {
        metric: "Sites total",
        formula:
          "Seven Hills + Laverton + O'Connors, converted at the product's ratio: Approved stock plus stock lines with no QC status entry in Ostendo. Ostendo keeps QC status per AO number and item, and Andrew's own stock exports count lines without one. It includes O'Connors whether or not that column is shown. It excludes Coopers Incoming, stock in transfer, and anything QC is holding.",
      },
      {
        metric: "In-Transfer",
        formula: "Counted, not converted: one open transfer line is one pallet.",
      },
      {
        metric: "Dockets under On Order and In-Transfer",
        formula:
          "Every open docket behind the figure, earliest required date first. A cell shows the first two; +N more opens the rest in place and less folds them away. The search box matches every docket, including the folded ones.",
      },
      {
        metric: "Notes",
        formula:
          "Today's bookings on a ScheduleIt production lane, matched to products the same way RM Ordering matches them (a pinned mapping, or the plant's product name or alias, exactly or as a prefix), with the start time and the quantity the dispatcher typed. A bulk-binder row is only credited by a title that says bulk, binder or 1000 kg. A booking that matches no product is listed under Production today only. Hover a run to see its booking title and how it matched.",
      },
    ],
    limitations: [
      "One board product shows ? no ratio: Dywidag Additive (210119), whose Crosbe National item description states no pallet configuration. IG 110 A+ LC (210118) converts on an assumed 80 bags per pallet and is marked ~ until a physical pallet count confirms it.",
      "If Crosbe National's item master cannot be read, pallet ratios come from the app's older copy of the descriptions and the page says so; on that copy a few more products show ? no ratio.",
      "Held stock is read for the board's own products only. Item codes Ostendo returned that are not on the stand-up board are listed in the All products view only, with their held figures shown as not read.",
      "The schedule is as fresh as its last sync, which runs on the schedule set by the sync cadence; the page shows the last sync time. A booking changed in ScheduleIt after that shows here after the next sync.",
      "A † before a site figure means a batch at that site is not yet updated in Ostendo.",
    ],
    version: "v0.2",
    lastUpdated: "2026-10-06",
  },

  "/monthly-summary": {
    title: "Monthly Production Summary",
    description:
      "Tonnes produced per product for a chosen calendar month, grouped by product category, with month-on-month and year-on-year comparisons and a grand-total budget comparison from Ostendo.",
    dataSources: [
      {
        name: "Production batches",
        table: "production_monitor_batch (weight_kg, product_lookup, production_time)",
        source: "Cloud SQL (live)",
        refresh: "KV cache: current month 5 min, last month 1 hr, older months 30 days",
      },
      {
        name: "Product categorisation patterns",
        table: "QA_PRODUCTS.productionLike (src/shared/products.ts)",
        source: "Static config, matched via ILIKE in SQL",
      },
      {
        name: "Qty budget (grand total)",
        table: "BUDGETDETAIL (BUDGETNAME '<FY> FY Qty Budget', PERIOD{N}VALUE)",
        source: "Ostendo ERP (CROS)",
        refresh: "Cached 1 hour",
      },
    ],
    calculations: [
      {
        metric: "Tonnes (this month)",
        formula: "SUM(weight_kg) ÷ 1000, bucketed by (production_time AT TIME ZONE 'Australia/Sydney')::date within the month",
        // Sydney-local bucketing was the GH #391 fix; the issue number stays here, not in user copy.
        notes: "Sydney-local day bucketing so it reconciles with the Production dashboard.",
      },
      {
        metric: "vs Prev Month / vs Prior Year %",
        formula: "((tonnes_this - tonnes_comparison) / tonnes_comparison) × 100",
        notes: "Null when the comparison period produced 0 tonnes.",
      },
      {
        metric: "vs Budget %",
        formula:
          "((tonnes_this - tonnes_budget) / tonnes_budget) × 100; tonnes_budget = SUM(PERIOD{N}VALUE) across all '<FY> FY Qty Budget' rows for the month's FY period",
        notes: "Australian FY (Jul-Jun): Jul=PERIOD1 … Jun=PERIOD12.",
      },
      {
        metric: "Category classification",
        formula: "Longest matching QA_PRODUCTS.productionLike ILIKE pattern wins; unmatched product_lookup → 'Uncategorised'",
      },
    ],
    limitations: [
      "Per-product and per-category budget is null in v1. Only the grand-total budget is wired, which needs an Item-Code to product_lookup mapping.",
      "Budget can read 'not loaded' / 'Ostendo unreachable' / 'CROS not configured' when the FY budget isn't loaded or CROS is down.",
      "Production is dated by batch-make while Ostendo budget is dated by invoice, so lagging-invoice months can look below budget until invoicing catches up.",
      "Closed historical months are cached up to 30 days; very recent backfills may not show until the cache expires (current month refreshes every 5 min).",
    ],
    version: "v1.0",
    lastUpdated: "2026-06-17",
  },

  "/compliance/clauses": {
    title: "Clause Register",
    description:
      "Read-only list of the quality system clauses assessed on DOCREF 307: the 28 ISO 9001:2015 clauses, then the 5 CARES scheme requirements ISO 9001 does not cover. Every clause reads Not assessed in this first slice.",
    dataSources: [
      {
        name: "Clause list",
        table: "ISO_9001_CLAUSES, CARES_REQUIREMENTS (src/shared/compliance-clauses.ts)",
        source: "Shipped constant, from DOCREF 307 rev 3",
        refresh: "Changes only when the code changes",
      },
    ],
    calculations: [],
    limitations: [
      "Status is fixed at Not assessed for every clause. Assessment, history and per clause evidence are not built yet.",
      "Clause titles follow the published ISO 9001:2015 headings, so wording can differ from DOCREF 307 until the form is checked against it.",
      "The two lists are shown separately, as the form does. There is no combined view of clauses scored under both schemes.",
    ],
    version: "v1.0",
    lastUpdated: "2026-09-12",
  },

  "/commercial/qc-vs-tds": {
    title: "QC vs TDS",
    description:
      "What each product's Technical Data Sheet (TDS) claims, against what QC measured in the chosen dates (the last twelve months by default). Six views: Compliance, Coverage, Claim vs measured, the Workbook view, Compare and Claims vs tests; hover a tab for what it shows. Only admins, Tech Services and the QC Manager can open this page; Tech Services and the QC Manager can edit the standards on Claims vs tests. Two products go by two names: CrosFlow 815 is the same product as CrosFlow 810 LC, and CrosFlow 835 the same as CrosFlow 830 LC.",
    dataSources: [
      {
        name: "QC results",
        source: "The plant QC records",
        refresh: "Live on every page load",
      },
      {
        name: "TDS figures",
        source: "A copy of Technical Services' TDS figures",
        refresh: "Not live: updated by hand, so it can lag a new TDS revision",
      },
      {
        name: "TDS revision and issue date",
        source: "As published on crosbe.com",
        refresh: "Live",
      },
      {
        name: "R&D trial results",
        source: "The mix design record",
        refresh: "Live on every page load",
      },
      {
        name: "Reference masses behind the percentages",
        source: "Each product's QC settings",
        refresh: "Live",
      },
      {
        name: "Standard or method for each test",
        source:
          "The standard and test method behind each claim, edited on Claims vs tests. A test with neither shows None recorded in red; one left blank shows Not recorded in grey. Which products quote each test comes from each product's current published TDS.",
        refresh: "Live",
      },
    ],
    calculations: [
      {
        metric: "Compliance grade",
        formula: "samples outside the TDS limit ÷ samples graded, per product and test",
        notes:
          "0% is within spec, up to 10% is minor and over 10% is critical. A cell with fewer than 5 samples is still graded but carries a dot, because so few samples are indicative only.",
      },
      {
        metric: "Which side fails",
        formula: "below a floor or above a ceiling, per test",
        notes:
          "Flow is one column, and each product is graded on its own flow test, the test its approval rule names: the flow table for Panfill, Pocket Patch, Crosfill 510 and the Crospatches; the efflux cone for PT Grout and the Infragrouts; the flow trough for Crosfill 530; both the table and the trough for Crosfill 580 HES; the flow ring at 20 minutes for Crosflow. The cell names the test it graded. Strength and flow in mm (table, trough, ring) fail only below the TDS figure, or below the lower figure of a range, so a stronger or more fluid batch never fails. Efflux is a time in seconds, so it fails only above the TDS figure, or above the higher figure of a range, because a faster time is a more fluid grout. A single figure for bleed, maximum particle size or a chloride, acid-soluble chloride, sulphate or nitrate limit is a ceiling. A single sand figure is the formula figure, and a reading fails when it is more than 3 points either side of it; a reading 2 to 3 points out is a warning and is not counted. Any other range fails on either side. The page works shrinkage out as a negative number for a sample that contracts, so a TDS figure of 600 fails one that contracts more than 600 microstrain; a sample that expands never fails.",
      },
      {
        metric: "Not evaluated",
        formula: "readings with no limit they can safely be graded against",
        notes:
          "A dashed outline on Compliance, with the reason on hover: no TDS limit; a single figure on a test with no direction, such as density; a claim in words, such as Approx. 70 min; a figure that cannot be read, such as a date; a figure more than 10 times away from the readings, which suggests a unit mismatch; or no released reading in the dates. The Not included note names every one with its reason, the longer lists folded behind a toggle that opens them in place; a set time unit mismatch is listed under Set time.",
      },
      {
        metric: "Outliers left out",
        formula: "a reading more than 5 times the median for that row and test",
        notes:
          "Taken as a data entry error and left out of every range and grade. The median is taken over every reading in the chosen dates, released or not, of the samples graded on that row. Hover a cell to see how many were left out.",
      },
      {
        metric: "QC range",
        formula: "the spread of the readings Range picks",
        notes:
          "Range has three choices: released batches only (the default); every reading, lowest to highest; or the 5th to 95th percentile. It sets the QC range on Claim vs measured, the Workbook view and Compare, and which rows the \"Only rows QC disagrees with\" box keeps, but not the grade. A replicate pair is averaged into one reading first, as Records and the control charts do.",
      },
      {
        metric: "Product order",
        formula: "category, then product name; on the Workbook view and Compare, the column heading last tapped",
        notes:
          "Products are listed by name within each category, reading numbers as numbers, so 9 comes before 100. Every view lists products in this order, with categories in Technical Services' order and water ratios lowest first, except Claim vs measured with Worst first on. Claims vs tests shows each product under its TDS name and puts it where Technical Services' sheet has that product, even when the sheet calls it something else. A product goes after the rest, by name, when the page cannot find it on that sheet: the sheet does not list it, or lists it under another name without saying which TDS it is. While the QC results are loading, Claims vs tests waits to show its product lists; if the QC results cannot be read, it lists products by name only and says so. Tap a heading to sort by that column and tap it again to reverse; rows always stay in their category. A range sorts by its lower figure, Compare sorts by the QC figure, and Period by the number of samples. Cells with no figure, such as N/A, no QC, not read or a claim in words, go last either way.",
      },
      {
        metric: "QC-backed",
        formula: "quoting products whose published figure came from QC results",
        notes:
          "Shown on Claims vs tests, which lists each test a published TDS can quote. A figure typed in by hand, or with no record of where it came from, is not counted.",
      },
      {
        metric: "Sand %, Bleed %, Expansion %",
        formula: "reading ÷ the product's reference mass × 100",
        notes: "The divisor is that product's own reference value, never a fixed bag weight.",
      },
      {
        metric: "Density (from SG), kg/m³",
        formula: "specific gravity × 1000",
        notes:
          "QC has no separate density test: it records specific gravity, so 2.02 shows as 2020 kg/m³. A TDS density recorded in t/m³ converts the same way, so 1.85 t/m³ reads as 1850.",
      },
      {
        metric: "Shrinkage",
        formula: "(28-day reading minus that sample's 24 hour reading) ÷ (gauge reference + 24 hour reading) × 1,000,000",
        notes: "Microstrain to AS 2350.13, worked out from the gauge readings sample by sample, because each sample has its own 24 hour baseline.",
      },
      {
        metric: "Bar shade on Claim vs measured",
        formula: "share of samples outside the TDS limit: none out, up to 10%, over 10%, over 40%",
        notes:
          "Green for none out, amber for up to 10%, red for over 10%, and a darker red with a light red edge for over 40%. A bar with nothing graded is grey. A very narrow bar is drawn a little wider than measured so its colour shows, centred on the reading, but never across a band edge the reading does not cross.",
      },
    ],
    limitations: [
      "Where a row has a TDS revision on file, it is checked against crosbe.com, but the row's figures are not read from the published document, so a matching revision does not mean every figure matches. A row with no revision on file shows only the revision crosbe.com publishes, because the page cannot tell which one its figures came from. For anything a customer sees, go by the TDS on crosbe.com.",
      "R&D figures are development trials, including attempts that did not work, so a wide spread is normal and not a spec problem.",
      "A material tested at more than one water ratio grades each QC sample on the row whose water range holds the water % written in the sample's notes, such as \"20% water\" or \"23% of water\". A sample whose notes give no water % is graded on the production ratio, and a figure that is an addition, such as \"+1% water post add\", is not read as the mix water. A sample noted at a water % outside every row is graded on no row and listed under the production row's name. The lab record has no water field yet, so a water % that is not written in the notes cannot be seen.",
      "Water on Coverage is outlined, not green or red: it is the dose a sample is mixed at, not a lab test, and the plant record has no water field to confirm it.",
      "Two things leave a reading out: the outlier rule, and a day when the plant held or rejected bags of that product. The grade always leaves those days out; Range leaves them out under released batches only. A bad batch that was released stays in, so check it before acting on a failure.",
      "Some TDS tests, such as Work time, Chloride and FRL, have no QC test on the line. Coverage marks them as never run in QC; the other views show the TDS figure with nothing under QC. When no product in view has a QC result for the chosen measures, every view but Coverage and Claims vs tests says so instead of drawing an empty grid. When there are QC results in view and none can be graded, Compliance and Claim vs measured say why instead. A note under Compliance with no graded result for its tests says so; it never says nothing fails. When its tests have no result in the dates, or every graded one has fewer than 5 samples, the note says that too. Each note says what it covers: the results in the chosen dates, only the rows shown while \"Only rows QC disagrees with\" is ticked, and only the results that were read while a product's QC cannot be read. A note lists only failures on 5 or more samples. When it has none to list, it counts the failures on fewer and points to the Small sample mark.",
      "A published TDS that cannot be read is left out of Claims vs tests and counted in a note above the table.",
      "When the plant QC records cannot be read, the page says so and draws no grid, so nothing reads as a pass. When some products' QC records cannot be read, a note above every view except Claims vs tests names them with Retry; their cells are hatched or say not read, and their QC is left out of every QC figure. With no QC results in the chosen dates, the page says so and asks for an earlier From date; when only the chosen Category has none, it says that instead.",
    ],
    version: "v1.36",
    lastUpdated: "2026-10-27",
  },

  "/test-monitor": {
    title: "Test Monitor 24h",
    description:
      "Live countdown screen for in-progress QC tests. One card per ticked test on recent samples, colour-coded by product, sorted closest-to-due first; a card flashes when its result is due and disappears when the result is entered. Setting Time clears only when both initial and final set results are entered, with a manual remove for abandoned entries.",
    dataSources: [
      {
        name: "Ticked test sets",
        table: "qa_sample_test_sets (D1)",
        source: "D1 (crosbe-auth)",
        refresh: "Live, 30s poll",
      },
      {
        name: "Sample results (completion probe)",
        table: "qa_production_*sample (per product)",
        source: "Cloud SQL (live)",
        refresh: "Live, 30s poll",
      },
      {
        name: "Admin removals",
        table: "test_monitor_removals (D1)",
        source: "D1 (crosbe-auth)",
      },
    ],
    calculations: [
      {
        metric: "Due time",
        formula: "sample_datetime + test age (Flow 20 = 20min, Bleed = 3h, Shore D = 4h/24h, Compression 24h = 24h); re-anchored by Test Date where set (#1662)",
      },
      {
        metric: "Flash time",
        formula: "due − 2min for Flow 20, due − 10min for Bleed / Shore D / Compression 24h; Setting Time never flashes",
      },
      {
        metric: "Ordering",
        formula: "remaining time ascending; Setting Time groups with the closest-to-due Crosflow entry, or sits at the very top when no Crosflow test is active",
      },
    ],
    limitations: [
      "Only samples from the last 30 hours appear. The screen is a live view, not a backlog.",
      "Entries derive from the ticked test set at sample entry; an unticked test never appears.",
      "Manual removal is intended for abandoned Setting Time entries that can no longer be completed.",
    ],
    version: "v1.0",
    lastUpdated: "2026-08-27",
  },
  "/reminders": {
    title: "Calendar",
    description:
      "Calendar and list of cube-breaking and QA testing reminders sourced from the legacy Django reminders system. Items can be toggled active/complete individually or in bulk (writes are blocked in read-only mode).",
    dataSources: [
      {
        name: "Reminders",
        table: "reminders_reminder JOIN django_content_type",
        source: "Cloud SQL (live)",
        refresh: "Live per page load",
      },
      {
        name: "Reminder products (content types)",
        table: "reminders_reminder JOIN django_content_type (distinct)",
        source: "Cloud SQL (live)",
      },
    ],
    calculations: [
      {
        metric: "Summary counts",
        formula: "active = COUNT(is_active = true), completed = COUNT(is_active = false), total = active + completed for the month",
      },
      {
        metric: "Product label",
        formula:
          "django_content_type.model mapped to a QA_PRODUCTS label by stripping 'sample'/'config' suffix; falls back to a humanised model name",
      },
      {
        metric: "Test abbreviation",
        formula: "field_reference mapped via FIELD_LABELS (e.g. compressive_strength_28d_a → 'C.S. 28d'); unknown refs shown raw",
      },
    ],
    limitations: [
      "Toggling or bulk-toggling a reminder is blocked by read-only middleware while connected to the production database.",
      "Product filter and labels come from django_content_type.model; products without a QA_PRODUCTS match show a best-effort humanised name.",
      "Only a fixed set of test field_references have friendly labels; others display the raw column name.",
      "Calendar groups by day-of-month from the reminder date; reminders are scoped to the selected month only.",
    ],
    version: "v1.0",
    lastUpdated: "2026-06-17",
  },

  "/artifacts": {
    title: "Gadgets",
    description:
      "Live mini-apps you keep: each gadget re-runs its data for whoever opens it, under that person's role. Start from a template or ask the Data Analyst to make one. Charts pinned from chat sit below, unchanged. To rename one of yours or edit its tags, use the pencil beside Delete.",
    dataSources: [
      {
        name: "Saved artifacts",
        table: "artifacts (D1, scoped to user_id)",
        source: "Crosbe AI D1 (DB_AUTH binding)",
        refresh: "Live per page load",
      },
      {
        name: "Refresh data",
        table: "spec regenerated by re-running the artifact's saved SQL",
        source: "Re-executed via /api/artifacts/{id}/refresh",
      },
    ],
    calculations: [
      {
        metric: "Render type",
        formula: "chart (spec has chart_type) → Recharts bar/line/pie/scatter; table (spec has columns) → data grid; else raw JSON",
      },
      {
        metric: "Time ago",
        formula: "now - created_at, shown as Xm/Xh/Xd ago, then a Sydney-local date past 7 days",
        notes: "created_at treated as UTC if it has no timezone suffix.",
      },
    ],
    limitations: [
      "Artifacts are per-user (D1 rows filtered by user_id), so you only see your own.",
      "Refresh is only available when the artifact has a stored sql_query; pinned static specs cannot be refreshed.",
      "Chart rendering requires data, an x_key, and at least one y_key in the spec, or it shows 'No chart data'.",
      "Deleting an artifact is permanent (a confirm dialog is the only guard).",
    ],
    version: "v1.0",
    lastUpdated: "2026-06-17",
  },


  "/production-monitor": {
    title: "Live Production View",
    description:
      "The unattended live production wallboard: per-mix-run time breakdown for the current planning run, current batch and total batches, planning number and planning total. Volume analysis and the Production list moved to Finished Goods as tabs. Costs and the Ostendo BOM recipe stay gated.",
    dataSources: [
      {
        name: "Live Dashboard",
        table:
          "production_monitor_batch, production_monitor_batchmaterial, production_monitor_formulation, production_monitor_product",
        source: "Cloud SQL (live, read-only)",
        refresh:
          "Every 120 seconds in the background; the last successful update remains visible during a brief outage",
      },
      {
        name: "Recent batches",
        table: "production_monitor_batch",
        source: "Cloud SQL (live)",
      },
      {
        name: "Batch material breakdown (on expand)",
        table: "production_monitor_batchmaterial",
        source: "Cloud SQL (live)",
      },
      {
        name: "Testing plan (QC approval per batch)",
        table: "production_monitor_batch + the per-product QA sample and spec tables",
        source: "Cloud SQL (live)",
        refresh: "Every 120 seconds, alongside the rest of the board",
      },
    ],
    calculations: [
      {
        metric: "Live shift production",
        formula:
          "SUM(weight_kg) from the 05:00 Australia/Sydney shift boundary, grouped by formulation product and shown in tonnes; hourly average divides by complete hours since 05:00 (or equals the total during the first hour)",
      },
      {
        metric: "Live shift-month production",
        formula:
          "SUM(weight_kg) from 05:00 on day 1 through 05:00 on day 1 of the next shift month; daily average divides by the current shift day-of-month",
      },
      {
        metric: "Batch approval",
        formula:
          "A batch is approved when a QC sample recorded against that product and batch number carries a reading for the product's approval test, and that reading sits inside the spec limits that applied to the sample. Which test approves a product is set per product on Spec Limits. A batch the testing frequency designates but which has no reading yet shows as waiting.",
      },
      {
        metric: "Latest planning total",
        formula:
          "SUM(weight_kg) for every batch sharing the latest batch planning_number, shown in tonnes",
      },
      {
        metric: "Lookup / formulation status",
        formula:
          "Lookup fails when the latest batch has no formulation or an unresolved material. Formulation fails when lookup fails or any recipe-expected minus batch-target material difference is non-zero; only the difference is exposed, not recipe quantities.",
      },
      {
        metric: "Material Variance (per batch)",
        formula: "variance_kg = actual_kg - target_kg; variance_pct = (actual_kg - target_kg) / target_kg × 100",
        notes: "Red over ±5%, amber over ±2%.",
      },
    ],
    limitations: [
      "QC-scoped by design: production volumes, status differences, and per-batch material tolerances only. Cost/pricing and recipe expected/target quantities are not shown.",
      "Live Dashboard uses the 05:00 Australia/Sydney production shift. Volume Analysis defaults to the last 90 days and buckets by Australia/Sydney local day.",
      "Material breakdown loads per batch on expand (one query per opened batch).",
      "The Production List tab shares the /production-list page body and opens on its dense Tolerance-detail view (last 14 days by default).",
    ],
    version: "v1.1",
    lastUpdated: "2026-08-17",
  },

  "/explore": {
    title: "Data Explorer",
    description:
      "Direct table browser for the production database. View any table's data with pagination, sorting, and column filtering.",
    dataSources: [
      { name: "All postgres tables", source: "Cloud SQL (live, read-only)" },
    ],
    calculations: [],
    limitations: [
      "Raw data view. No calculated fields or joins.",
      "Max 100 rows per page.",
      "Some tables have many columns, so horizontal scroll is required.",
    ],
    version: "v2.0",
    lastUpdated: "2026-03-28",
  },

  "/admin": {
    title: "Admin",
    description:
      "User and role administration plus operational panels: who can sign in, what each role can see and do, email subscription routing, external digest recipients, manual data syncs with their run log, and device telemetry.",
    dataSources: [
      { name: "Users, roles and permissions", source: "Application database" },
      { name: "Sync run log", source: "Application database, one row per sync run" },
      { name: "AI usage", source: "Application database, recorded token counts and calls" },
    ],
    calculations: [{ metric: "AI tokens", formula: "Recorded input + cache creation + cache reads + output", notes: "For Anthropic, recorded input excludes the separately recorded cache tokens. Other providers may include cached input in their input counter without a separate cache breakdown. These are recorded volumes, not cost estimates: the total is a token count, not a cost. Cache reads are billed at a fraction of the input rate, and cache creation costs a little more than input." }],
    limitations: [
      "Role edits take effect on the user's next page load. No restart is needed, but an open session keeps its current view until then.",
      "The manual sync buttons run the same code as the nightly schedule, so firing one early never causes a double-apply.",
    ],
    version: "v1.0",
    lastUpdated: "2026-08-05",
  },

  "/admin/material-mapping": {
    title: "Material Mapping",
    description:
      "Resolve plant lookup codes to Ostendo items so their mass gets costed. Unmapped codes are the reason a batch's cost can read low. This page is where that gets fixed.",
    dataSources: [
      { name: "Mappings", source: "Application database, authored here" },
      { name: "Plant lookup codes and Ostendo items", source: "Production records and the ERP item master" },
    ],
    calculations: [],
    limitations: [
      "A new mapping applies from the next cost computation. Historical figures don't retro-correct automatically.",
      "Deleting a mapping returns its code to the unmapped list; it doesn't delete any production data.",
      "Writing to plant tables: confirmed mappings land in the plant's own material/lookup tables, so future batches resolve at ingest and the code prices immediately. If the plant-table write grants are missing, confirming is disabled but the queue and existing mappings stay readable.",
      "Migrate mappings to plant tables backfills curated cost-map codes into the plant's own material/lookup tables (materials the cost map knows a code for but the plant row doesn't carry yet). It is dry-run first; nothing is written until the plan is confirmed.",
      "Product mapping covers the product's own Ostendo code (identity, fill-blank only) and its name aliases for the \"same product, different name\" case (e.g. a sheet name for a plant product). Costing links to finished-good SKUs live on the BOM Explorer, not here.",
    ],
    version: "v1.1",
    lastUpdated: "2026-09-13",
  },

  "/admin/ostendo-access": {
    title: "Ostendo Access",
    description:
      "The roles × ERP-resources matrix: exactly which Ostendo data each role can reach, resource by resource, database by database.",
    dataSources: [
      { name: "Role grants and resource catalog", source: "Application database" },
    ],
    calculations: [],
    limitations: [
      "Each tick grants one resource on one database (COOP or CROS). A resource that exists on only one database has one tick.",
      "A role reads a ticked resource through the Data Analyst only while it also holds that database's Analyst tool, shown under the role name. A tick the role cannot use for that reason carries an unplugged mark.",
      "An unticked cell with a dashed outline and a link mark is still readable through a capability the role holds (hover names it). Unticking does not remove that; removing a capability grant is an operator change.",
      "Changes take effect immediately for new requests and are recorded with who made them; re-test the affected role's pages after any edit.",
    ],
    version: "v1.2",
    lastUpdated: "2026-10-05",
  },

  "/admin/ai-feedback": {
    title: "AI Feedback",
    description:
      "How the Data Analyst is landing: thumbs up/down ratings over time, conversations flagged as looking frustrated so they can be read and learned from, and the access requests people have asked the Analyst to file.",
    dataSources: [
      { name: "Ratings and flagged conversations", source: "Application database, captured from Analyst usage" },
      { name: "Open access requests", source: "Filed by the Analyst when someone's role blocks what they asked for" },
    ],
    calculations: [
      { metric: "Frustration flag", formula: "heuristic over conversation shape (repeats, corrections, abandonment), a triage aid, not a verdict" },
    ],
    limitations: [
      "Unrated conversations vastly outnumber rated ones, so treat ratings as a signal, not a satisfaction survey.",
      "Flagged conversations contain real user queries. Read them with the same care as any user data.",
      "Marking a request resolved records the decision; it does not grant anything. Change the role or the overrides in Admin first.",
    ],
    version: "v1.1",
    lastUpdated: "2026-09-10",
  },

  "/portal-admin": {
    title: "Portal Admin",
    description:
      "Manage customer access to the QC portal: which customers have live portal links, what each link exposes, and revocation.",
    dataSources: [
      { name: "Portal tokens and customer scopes", source: "Application database" },
    ],
    calculations: [],
    limitations: [
      "A portal link is bearer access: anyone holding the link sees that customer's view. Revoke and reissue if a link may have leaked.",
      "Revocation is immediate; the customer's next request gets a clean denial page.",
    ],
    version: "v1.0",
    lastUpdated: "2026-08-05",
  },

  "/commercial/pipeline": {
    title: "Sales Pipeline",
    description:
      "Expected tonnes by customer and month. Sales log what they expect to sell and when; the tonnes are spread evenly across the months covered, and a month is ticked once the sale is confirmed. An entry can name its project, shown beside the customer, so two jobs for the same customer read apart. The Planner tab shows the same board as a month-by-month grid, with a row for each of a customer's projects.",
    dataSources: [
      {
        name: "Pipeline entries",
        source: "Entered by the sales team on this page",
        refresh: "Live, every change is visible to the whole team immediately",
      },
      {
        name: "Customer name suggestions",
        source: "Active customer accounts",
      },
    ],
    calculations: [
      {
        metric: "Monthly tonnes for an entry",
        formula: "tonnes ÷ number of months the entry covers",
        notes: "An even spread. A sale with a real month-by-month shape is better logged as separate entries.",
      },
      {
        metric: "Pipeline tonnes",
        formula: "sum of every monthly slice that has not been confirmed",
      },
      {
        metric: "Confirmed sales",
        formula: "sum of every monthly slice that has been ticked as confirmed",
      },
    ],
    limitations: [
      "These are expectations entered by hand, not orders. They are only as good as the latest update from the sales team.",
      "Tonnes spread evenly across the months chosen; a sale that lands mostly in one month is better logged on its own for that month.",
      "Confirming a month does not check it against what was actually invoiced.",
    ],
    version: "v1.2",
    lastUpdated: "2026-10-30",
  },

  "/commercial/competitor-prices": {
    title: "Competitor Prices",
    description:
      "Competitor prices the sales team has seen in the field, on one shared list. Log a price with the manufacturer, product, state, customer type, unit and the day you saw it; fix a mistake with Edit; search and filter the list, sort any column, and export what you are looking at to Excel.",
    dataSources: [
      {
        name: "Price entries",
        source: "Entered by the sales team on this page, plus earlier tracker sheets loaded in by hand",
        refresh: "Live, every entry is visible to the whole team as soon as it is saved",
      },
    ],
    calculations: [
      {
        metric: "$ per tonne",
        formula: "price × 50 for a 20 kg bag, price × 40 for a 25 kg bag, the price itself for a tonne",
        notes: "Blank for a pallet or other unit, because the tonnes in those are not known.",
      },
    ],
    limitations: [
      "Prices are what someone saw and wrote down, ex GST. They are not checked against an invoice or a price list.",
      "The same price for the same product, state, customer type and unit on the same day is one entry; logging it twice is refused.",
      "You can edit and delete entries you added. The Sales Manager can edit and delete any entry. A deleted entry is kept on the record, not erased.",
      "An edit keeps who added the entry and when, and records who edited it, when, and the values before and after. The entry shows the latest edit.",
      "If someone else saves an entry while you have it open, your save is refused and what you typed stays in the form.",
      "Open to the Sales Manager, the sales reps and CEO/Operations. Reps and CEO/Operations add prices and edit or delete their own; only the Sales Manager edits or deletes anyone's.",
    ],
    version: "v1.1",
    lastUpdated: "2026-10-06",
  },

  "/commercial/troubleshooting": {
    title: "Troubleshooting",
    description:
      "On-site product support. The guided flow takes a product and symptom to a likely cause, a recommended action, and the product's TDS; the chat hands harder cases to Crosbe AI; and flagged cases queue for Technical Services review.",
    dataSources: [
      {
        name: "Guided-flow questions, causes and actions",
        source: "Curated troubleshooting knowledge, maintained by Technical Services",
      },
      {
        name: "AI chat",
        source: "Crosbe AI with product context",
      },
    ],
    calculations: [],
    limitations: [
      "The guided flow covers known symptom patterns. An unlisted symptom goes to chat or a flag, not a dead end.",
      "A flagged case is a queue entry for a person, not an instant answer; the flag notice says what happens next.",
      "Advice quality follows the curated content. If an answer looks wrong on site, flag it so the content gets fixed.",
    ],
    version: "v1.0",
    lastUpdated: "2026-08-05",
  },

  "/commercial/cases": {
    title: "Tech Services",
    description:
      "Technical Services and Commercial cases from one board. A case is a reported problem or inquiry from a customer, engineer or site, tracked from Open through the working statuses to Resolved, with the batches involved, site visits, actions and attachments on the record.",
    dataSources: [
      {
        name: "Cases, sites, visits and actions",
        source: "Entered in this app by Technical Services, sales reps and QC",
      },
      {
        name: "Accounts",
        source: "CROS customers synced nightly, plus prospects added here",
      },
    ],
    calculations: [
      {
        metric: "First response target",
        formula: "24 hours from when the case was received, for every inquiry class",
      },
      {
        metric: "Resolution target",
        formula: "Minor 4 hours, Moderate 24 hours; Major is measured by NPS instead",
      },
    ],
    limitations: [
      "A case leaves Open only once it has an inquiry class, and resolves only once an outcome is written.",
      "Only Technical Services changes status, class, responsible, observations and outcome; whoever opened a case can edit its other details.",
      "The export is the current board as a spreadsheet; it does not include visit photos.",
      "A batch typed on a visit is an assembly order (AO and 7 digits) or an AO tail. It is added to the linked case once the visit saves.",
    ],
    version: "v1.1",
    lastUpdated: "2026-09-24",
  },

  // #3355: the Tech Services tab strip (?tab=board|qr|presentations|specifications|report).
  // The Cases tab (key board) is the page doc above; QR codes is the one other
  // tab with content today. Presentations, Specifications and Report arrive
  // with EVENTREG, SPECPIPE and TSREPORT and add their own `?tab=` doc then.
  "/commercial/cases?tab=qr": {
    title: "QR codes",
    description:
      "A QR code per presentation, brochure or site board, each pointing at a crosbe.ai short link. The code never changes once printed; the page it opens can be changed here at any time.",
    dataSources: [
      {
        name: "QR codes and scan counts",
        table: "qr_links",
        source: "Entered in this app by Technical Services",
      },
    ],
    calculations: [
      {
        metric: "Scans",
        formula: "One per page load of the short link; link checkers and previews that only ask for headers are not counted",
      },
    ],
    limitations: [
      "A code can only open a page on crosbe.ai, crosbe.com or crosbe.app.",
      "Scans are counted, not who scanned: no address, device or location is kept.",
      "A turned-off code shows a not-active page until it is turned back on.",
    ],
    version: "v1.0",
    lastUpdated: "2026-09-24",
  },

  "/carbon-exec": {
    title: "Carbon (Exec)",
    description:
      "Per-tonne CO2e by product, A1-A3 cradle-to-gate. Product-level totals only, built for board and customer conversations; this view carries no ingredient-level breakdown.",
    dataSources: [
      {
        name: "Per-product CO2e",
        source: "Computed from each product's formulation against AusLCI emission factors, served as product totals",
      },
    ],
    calculations: [
      {
        metric: "CO2e per tonne",
        formula: "sum of (ingredient mass share × AusLCI A1-A3 emission factor) per tonne of product",
      },
    ],
    limitations: [
      "A1-A3 only (materials, transport to gate, manufacturing). Use, transport-to-site and end-of-life stages are out of scope.",
      "Figures move when a formulation changes or emission factors are updated, so quote the date with the number.",
    ],
    version: "v1.0",
    lastUpdated: "2026-08-05",
  },

  "/bom-updates": {
    title: "BOM Updates & Approval",
    description:
      "The monthly BOM review. Every proposed change sits in a pending queue until an approver selectively commits it into a named, immutable version. The impact preview runs the same cost engine as the Explorer, so 'what will this do' and 'what did it do' can never disagree.",
    dataSources: [
      {
        name: "Pending changes, versions and review log",
        source: "Application database; versions are immutable once committed",
      },
    ],
    calculations: [
      {
        metric: "Impact preview",
        formula: "the selected subset of pending changes overlaid on the current BOM, cascaded through the cost engine to product totals",
      },
    ],
    limitations: [
      "Approving commits only the changes you selected. The rest stay pending.",
      "A committed version cannot be edited; correcting a mistake means a new version.",
      "The review log records who approved what and when. It's the audit trail, not a scratchpad.",
    ],
    version: "v1.0",
    lastUpdated: "2026-08-05",
  },

  // #3690: the five other BOM Suite tabs are access aliases of /bom-updates,
  // so each documents itself under a tab key the suite shell sets
  // (bomSuiteDocTab in src/shared/bom-suite.ts). Titles are the tab names.
  "/bom-updates?tab=summary": {
    title: "Summary",
    description:
      "Cost and margin per finished product. Standard cost comes from the same cost engine as Product BOMs, forecast mode included. The transfer price is the product's STDSELLPRICE in COOP Ostendo and the sell price its STDSELLPRICE in CROS Ostendo, as loaded into this page. Tap a price to see where it came from.",
    dataSources: [
      { name: "Standard cost per product", source: "Computed by the BOM cost engine from the current BOM inputs" },
      {
        name: "Transfer and sell prices",
        source: "Ostendo STDSELLPRICE (COOP for transfer, CROS for sell), copied in on 24 Sep 2026, or a manual load; each transfer price is checked against COOP",
      },
    ],
    calculations: [
      { metric: "Cooper margin", formula: "transfer price − standard cost" },
      { metric: "CN margin", formula: "sell price − transfer price" },
    ],
    limitations: [
      "A transfer price that differs from the Ostendo standard sell is flagged with the difference, never adopted.",
      "Sell prices are not re-read from CROS Ostendo: a change there shows here only after a new load.",
      "The load record stores no source, so the 24 Sep 2026 Ostendo backfill is recognised by its date.",
      "Movement against the previous BOM version is not shown: a version stores its inputs, not each product's outputs.",
    ],
    version: "v1.1",
    lastUpdated: "2026-10-23",
  },

  "/bom-updates?tab=explorer": {
    title: "Product BOMs",
    description:
      "Products compared side by side, built up from each product's current formulation: recipe share, kilograms per tonne after wastage, and cost per bag, with labour and overheads in the footer.",
    dataSources: [
      { name: "Recipes", source: "Each linked product family's current formulation" },
      { name: "Material prices", source: "Ostendo standard costs, or pending forecast prices in forecast mode" },
      { name: "SKU links", source: "Which finished-good SKUs each formulation family costs against, kept in the SKU links panel" },
    ],
    calculations: [
      { metric: "kg per tonne", formula: "the formulation's kg per tonne × the wastage factor" },
      { metric: "$ per bag", formula: "kg per tonne × $ per kg × pack kg ÷ 1,000, summed over materials, plus labour and overheads" },
    ],
    limitations: [
      "A product with no SKU link has no recipe to cost until it is linked.",
      "A material with no price shows N/A and leaves the product total incomplete.",
    ],
    version: "v1.0",
    lastUpdated: "2026-10-05",
  },

  "/bom-updates?tab=masterbatch": {
    title: "Masterbatch BOMs",
    description:
      "In-house blended additive packs, side by side, costed per tonne. The output $ per kg here is the price every product using that masterbatch pays on Product BOMs.",
    dataSources: [
      { name: "Masterbatch recipes", source: "Ostendo masterbatch BOMs, quoted per kilogram and shown per tonne" },
      { name: "Constituent prices", source: "Ostendo standard costs, or pending forecast prices in forecast mode" },
    ],
    calculations: [
      { metric: "Output $ per kg", formula: "constituent cost plus labour at the blending rate plus bulk electricity and bag, per kilogram" },
    ],
    limitations: [
      "A masterbatch without its blending rate (batches per hour) shows its components but no output $ per kg.",
    ],
    version: "v1.0",
    lastUpdated: "2026-10-05",
  },

  "/bom-updates?tab=map": {
    title: "Map",
    description:
      "The whole BOM as three columns: products, masterbatches and raw materials, joined by lines. Select any item to show everything it connects to and dim the rest.",
    dataSources: [
      { name: "Products, masterbatches and materials", source: "The same linked products, recipes and costs Product BOMs and Masterbatch BOMs read" },
    ],
    calculations: [],
    limitations: [
      "By default only product to masterbatch and masterbatch to material lines are drawn; select an item to see its direct material lines too.",
    ],
    version: "v1.0",
    lastUpdated: "2026-10-05",
  },

  "/bom-updates?tab=inputs": {
    title: "Inputs",
    description:
      "Labour and production rates and raw material costs, the inputs every BOM is costed from. Standard values change only at a BOM review; a forecast typed here is queued, with a reason, for the next review.",
    dataSources: [
      { name: "Labour and production rates", source: "The BOM settings store; a standard value changes only at a BOM review" },
      { name: "Raw material costs", source: "Ostendo last receipted and standard costs for active raw materials" },
    ],
    calculations: [
      { metric: "Δ FC/Std", formula: "(forecast − standard) ÷ standard × 100" },
    ],
    limitations: [
      "A queued forecast changes nothing until an approver commits it on Updates.",
      "Unused materials are hidden until you choose to show them.",
    ],
    version: "v1.0",
    lastUpdated: "2026-10-05",
  },

  "/bom-updates?tab=integrity": {
    title: "Data integrity",
    description:
      "The BOM Suite's standard costs beside Ostendo's, for every raw material on Inputs and every linked product, so you can check the two systems hold the same figures. A standard changed in Ostendo without going through the BOM Suite shows as a flag.",
    dataSources: [
      { name: "BOM Suite standards", source: "A material's published BOM Suite standard; the Summary's STD Cost for a product" },
      { name: "Ostendo standard cost and its date", source: "Ostendo's item list, read live on the same 15-minute read BOM Inputs uses, or the nightly copy when live is unavailable" },
      { name: "Ostendo BOM rollup and its date", source: "Ostendo's BOMs from the nightly copy" },
    ],
    calculations: [
      { metric: "Variance", formula: "BOM Suite figure minus Ostendo figure; zero is matched" },
      { metric: "Flagged", formula: "any variance not zero, or a product whose Ostendo standard differs from its own Ostendo rollup" },
      { metric: "Uses Ostendo's standard", formula: "a material with no approved BOM revision: the BOM Suite holds Ostendo's figure, so there is nothing to compare" },
    ],
    limitations: [
      "Materials compare to a millionth of a dollar, products to the cent.",
      "A missing input reads as incomplete, never as zero.",
      "Until a BOM revision is approved for a material, the BOM Suite's standard for it is Ostendo's own. It reads \"Uses Ostendo's\" with no variance (n/a), is neither flagged nor incomplete, and the count of such materials shows once above the table. Once a revision is approved the two standards are compared.",
      "Who changed a standard comes from Ostendo's cost history for flagged materials only; products show the date alone.",
      "Ostendo's figures come from the 15-minute read BOM Inputs shares, so the tab spends no live read of its own. Refresh shows the newest one; the column header says when it was read. A change made in Ostendo shows within 15 minutes.",
    ],
    version: "v1.0",
    lastUpdated: "2026-11-04",
  },

  "/costs/rm-purchases-fy26": {
    title: "RM Purchases",
    description:
      "Supplier purchase summary for a chosen period, the financial year to date by default: what raw materials were received, from whom, in what quantities and at what cost. Export to Excel downloads the same figures for the same period.",
    dataSources: [
      {
        name: "Raw-material receipts",
        source: "Ostendo ERP receipt records, queried live",
      },
    ],
    calculations: [
      {
        metric: "Supplier and material totals",
        formula: "sum of receipt quantities and values across the chosen period",
      },
    ],
    limitations: [
      "Up to 12 months at a time.",
      "Receipts, not orders: an ordered-but-undelivered purchase isn't counted yet.",
      "Values are as recorded at receipt; later credit notes or corrections show only if entered in the ERP.",
    ],
    version: "v1.0",
    lastUpdated: "2026-08-05",
  },

  "/updates": {
    title: "Updates",
    description:
      "What's new and what's next. The What's New tab is the changelog of shipped features and fixes; the Ideas tab collects feature requests with voting, so the loudest needs surface. An admin approves an idea, which hands it to the build team and marks it Planned, or rejects it, which marks it Declined. A card marked Shipped means it shipped.",
    dataSources: [
      {
        name: "Changelog entries and idea cards",
        source: "Application database, written when features ship or requests are filed",
      },
    ],
    calculations: [],
    limitations: [
      "Votes rank ideas; they don't schedule them. A high vote count is a strong signal, not a queue position.",
      "If something you reported was fixed but its card still looks open, say so in the card. Closing the loop is part of the fix.",
      "An approved idea turns Shipped within about an hour of the build team finishing it.",
    ],
    version: "v1.1",
    lastUpdated: "2026-10-23",
  },

  "/data-sheets": {
    title: "Data Sheets",
    description:
      "The current issued Technical Data Sheet for each product, to view or download. Every signed-in role can open it. Drafts and earlier revisions stay in the TDS Editor.",
    dataSources: [
      { name: "Issued TDS revisions (the PDF published from the TDS Editor)", source: "Application database and document store" },
      { name: "crosbe.com data sheets, where no revision has been issued in the app", source: "Crosbe website (verified copy)" },
    ],
    calculations: [],
    limitations: [
      "A product appears only once it has an issued data sheet or a verified copy on crosbe.com.",
      "Test certificates are not here yet.",
    ],
    version: "v1.0",
    lastUpdated: "2026-10-14",
  },

  "/guide": {
    title: "User Guide",
    description:
      "How to use the dashboards and what the numbers mean: a reading companion for the whole app. For per-page specifics, each page also carries its own 'About this page' panel like this one, at the bottom.",
    dataSources: [
      { name: "Guide content", source: "Written and maintained in the app itself" },
    ],
    calculations: [],
    limitations: [
      "The guide explains concepts; the per-page panels state exact formulas and data sources. Check both before trusting a number's meaning.",
    ],
    version: "v1.0",
    lastUpdated: "2026-08-05",
  },

  "/settings": {
    title: "Settings",
    description:
      "Your account and preferences: display name, appearance, and notification/email preferences. Changes here affect only you.",
    dataSources: [
      { name: "Your profile and preferences", source: "Application database" },
    ],
    calculations: [],
    limitations: [
      "Email address comes from your sign-in account and isn't editable here.",
      "Email subscription changes can take until the next scheduled send to apply.",
    ],
    version: "v1.0",
    lastUpdated: "2026-08-05",
  },

  "/calendar": {
    title: "Production Calendar",
    description:
      "A 12-month heatmap of daily production intensity: the year's rhythm at a glance, busy runs, quiet stretches and days with no production at all.",
    dataSources: [
      {
        name: "Daily production totals",
        table: "production_monitor_batch",
        source: "Cloud SQL (live)",
      },
    ],
    calculations: [
      {
        metric: "Daily intensity",
        formula: "sum of batch weights per Australia/Sydney local day, shaded relative to the year's range",
      },
    ],
    limitations: [
      "Days bucket by Sydney local time, so the calendar agrees with the Production dashboard.",
      "A blank day means no batches recorded, which can be a genuine non-production day or a data gap; cross-check Production for the date.",
    ],
    version: "v1.0",
    lastUpdated: "2026-08-05",
  },

  "/qa-upi": {
    title: "Batch Release / Approvals",
    description:
      "Create and manage AO tail batch records, the production identity a bag run carries through QC, and attach AO tails to test samples so results trace back to the batch they came from.",
    dataSources: [
      {
        name: "AO tail records",
        table: "qa_production_upi",
        source: "Cloud SQL (live)",
      },
      {
        name: "QC samples (for attachment)",
        table: "qa_production_*sample tables",
        source: "Cloud SQL (live)",
      },
      {
        name: "AO created (when each AO tail's assembly order was raised)",
        table: "AssemblyHeader.SYSDATECREATED",
        source: "Ostendo COOP: recorded by the auto-create job from its live read, otherwise the overnight copy",
      },
    ],
    calculations: [],
    limitations: [
      "The AO tail date is a plain date, shown as entered, with no timezone conversion and no midnight off-by-one.",
      "AO created is Sydney time as Ostendo stores it: the time alone when the AO was raised on the AO tail date, the date and time when it was another day, and the full value on hover. A value in a shape the page does not read is shown as stored. A row the system creates, or QC enters, for an AO dated in the auto-create job's live window (from the day before its last good quarter-hour run, at most 14 days back) shows its time within about a quarter hour; a row for an AO dated before that window and raised today stays blank until the next morning's Ostendo copy. A cancelled AO shows no time, and nor does a row two AOs under its number both fit by date (the AO's order date within a year of the row's), since the page cannot tell which batch the row is.",
      "Bag counts entered here feed QC Stats tonnage, so a wrong count here reads as wrong tonnes there.",
      "AO tail records are created and edited here, not deleted. A mistyped record gets corrected, so history stays attached.",
    ],
    version: "v1.1",
    lastUpdated: "2026-11-02",
  },

  "/rdti": {
    title: "RDTI Time",
    description:
      "R&D Tax Incentive time capture. Log hours against registered R&D projects and tasks, approve weeks, walk project eligibility gates, and attach evidence breadcrumbs, the record the RDTI claim is built from.",
    dataSources: [
      {
        name: "Time entries, projects, tasks and evidence crumbs",
        table: "rdti_entry, rdti_project, rdti_task, rdti_crumb",
        source: "Cloud SQL (live)",
      },
      {
        name: "Clockify import",
        source: "Clockify timesheets, imported and reconciled; imported entries carry their basis until reviewed",
      },
      {
        name: "Mix families linked to projects, and lab activity per family",
        table: "rdti_project_link, research_product, research_mixdesign",
        source: "Cloud SQL (live)",
      },
    ],
    calculations: [
      {
        metric: "Week totals",
        formula: "sum of entry hours per person per week; approval locks the week",
      },
      {
        metric: "Family candidates",
        formula:
          "for one project and window: mix designs saved by people who logged time to that project, on the same factory day (Australia/Sydney) they logged it; ranked by person-days, then designs, then hours; families already linked are left out; nothing is stored until a person confirms one",
      },
      {
        metric: "Mix-family coverage",
        formula:
          "per family with mix designs in the window: the projects linked to it and the hours (all entry statuses) logged to those projects in the window; per project with hours: how many of its linked families saw lab activity",
      },
    ],
    limitations: [
      "Approval locks each confirmed entry in the week (submit → confirm → approve). It never silently converts an entry you haven't submitted.",
      "Imported Clockify entries carry an unreviewed basis until someone confirms their project/task mapping.",
      "Eligibility gate-walk answers currently overwrite prior walks. No per-walk history yet.",
      "A family candidate matches a person's lab account by the RDTI identity map, else by a unique email match; a person with no match contributes no designs, and the dialog says how many were left out.",
      "A family with lab activity and no linked project is a candidate to confirm or dismiss, not a finding that a claim was missed.",
    ],
    version: "v1.1",
    lastUpdated: "2026-10-23",
  },

  "/projects": {
    title: "Projects",
    description:
      "Cross-cutting containers for R&D programs. Each project gathers its timeline, trials, concerns, members, shipments and evidence in one place, so a program's story isn't scattered across pages.",
    dataSources: [
      {
        name: "Projects and their timeline, concerns, evidence and membership",
        source: "Application database, authored in-app",
      },
    ],
    calculations: [],
    limitations: [
      "A project references data elsewhere (batches, mix designs, documents). Deleting the source doesn't delete the reference; it dangles visibly.",
      "Membership controls who sees a project's contents; an empty list here can simply mean no projects include you yet.",
    ],
    version: "v1.0",
    lastUpdated: "2026-08-05",
  },

  "/commercial/deliveries": {
    title: "Daily Dashboard",
    description:
      "The latest trading day's sales activity: what was ordered, how much, and where it went. Built for the morning read: yesterday's deliveries and pickups with the ship-to detail that the ERP reports leave out.",
    dataSources: [
      {
        name: "Sales invoices and lines",
        table: "SalesInvoiceHeader + SalesInvoiceLines",
        source: "Ostendo ERP (CROS), queried live",
      },
      {
        name: "Data freshness",
        source: "The 'Updated' badge shows when the ERP data was last refreshed, in factory (Sydney) time",
      },
    ],
    calculations: [
      {
        metric: "Day totals",
        formula: "sum of invoice line quantities and values for the selected trading date",
      },
    ],
    limitations: [
      "Shows invoiced activity, so an order that hasn't been invoiced yet isn't on the board.",
      "The trading date follows the invoice date in the ERP, which can lag a physical delivery.",
      "Rows reflect what you're set up to see; totals are for the rows shown.",
    ],
    version: "v1.0",
    lastUpdated: "2026-08-05",
  },

  "/commercial/customers": {
    title: "Customer List",
    description:
      "The customer book from the ERP (code, name, location and contact), grouped by salesperson with the largest book first. A lookup surface: find the account, then follow it into deliveries or aged debtors.",
    dataSources: [
      {
        name: "Customers",
        source: "Ostendo ERP (CROS) customer master, cached snapshot",
      },
    ],
    calculations: [],
    limitations: [
      "A snapshot, not live. The header notes the source; recent ERP edits can take up to a day to appear.",
      "Customers with no assigned salesperson group into an unassigned bucket at the end rather than disappearing.",
      "Contact details are only as good as the ERP record. Fix them in Ostendo, not here.",
    ],
    version: "v1.0",
    lastUpdated: "2026-08-05",
  },

  "/commercial/aged-debtors": {
    title: "Aged Debtors",
    description:
      "Who owes what, aged by invoice month against month-end payment terms: the board-level view of accounts receivable. Each account shows its aging buckets, credit limit utilisation, terms, and credit status.",
    dataSources: [
      {
        name: "Open receivable transactions",
        table: "CustomerAgingDetail",
        source: "Ostendo ERP (CROS), queried live",
      },
      {
        name: "Terms, credit limit and credit stop",
        source: "Ostendo ERP customer and invoice records, joined server-side",
      },
    ],
    calculations: [
      {
        metric: "Aging buckets",
        formula: "open transaction amounts grouped by the month the invoice was generated",
        notes: "Bucket tooltips show the exact invoice date range they cover.",
      },
      {
        metric: "Limit utilisation",
        formula: "outstanding balance ÷ credit limit",
      },
    ],
    limitations: [
      "Only accounts with a non-zero balance appear; the header counts them.",
      "Aging is by invoice month against month-end terms, so an invoice can be 'current' for most of two months before it ages.",
      "Tiny negative remainders from ERP rounding are cleaned up rather than shown as a confusing −$0.",
    ],
    version: "v1.0",
    lastUpdated: "2026-08-05",
  },

  "/commercial/aged-debtors-reminders": {
    title: "Debtor Reminders",
    description:
      "Payment reminders for overdue accounts and accounts close to their credit limit. Set the days overdue at which each reminder goes out, the low-credit amount, and the wording. Each morning the reminders due are listed here as drafts in the Outbox, where each one can be reviewed, its wording edited, and approved or rejected. Each draft shows who it would go to: the customer's email addresses from Ostendo, cc the account's salesperson and sales@crosbe.com, with replies going to the credit manager. An approved reminder is emailed only when someone presses Send, and only once sending has been switched on. Each decision and each send records who made it and when.",
    dataSources: [
      {
        name: "Overdue balances and credit limits",
        source: "The Aged Debtors page's figures, from Ostendo ERP (CROS)",
        refresh: "Drafts are listed once each morning; Preview works out today's list on demand",
      },
      {
        name: "Customer email addresses and salesperson",
        source: "Ostendo ERP (CROS) customer record; the salesperson's address is the crosbe.ai user linked to that salesperson",
        refresh: "Looked up when a draft is opened, and again when it is sent",
      },
      {
        name: "Settings",
        source: "Saved on this page",
      },
    ],
    calculations: [
      {
        metric: "Days overdue",
        formula: "today − the due date of the account's oldest overdue invoice",
        notes: "Due date is the end of the invoice month plus the payment terms, the same as Aged Debtors.",
      },
      {
        metric: "Credit remaining",
        formula: "credit limit − balance owed",
        notes: "Accounts without a credit limit never get a low-credit reminder.",
      },
    ],
    limitations: [
      "Sending is off until Jezweb switches it on. In test mode Send emails the reminder to you, not the customer. The switch cannot be changed on this page.",
      "Before sending, the balance is checked again. If it has been paid, or the invoice the reminder names is no longer the oldest overdue one, the reminder is skipped, not sent. If it has been part paid, the reminder is not sent: it comes back to To review showing the new balance, with the amount in its wording updated where it can tell which figure that is. It can be approved again once its wording states the amount overdue now and no longer states an earlier amount anywhere, a credit figure that reads the same included.",
      "Each approval sends a reminder at most once. A send that failed, or started and was never confirmed, is never retried on its own. Mark it not sent, with a reason, to put it back in To review; it then needs approving again before Send. A send that started under 10 minutes ago cannot be marked not sent yet.",
      "A customer with no email address in Ostendo cannot be sent a reminder; no address is guessed.",
      "Reminders carry no bank details or payment links, and wording that includes them cannot be saved or sent.",
      "Approve and reject are final for a draft until it comes back to To review. An edit keeps the draft waiting until it is approved or rejected.",
      "Each overdue account gets one draft per reminder stage while the same invoice stays its oldest overdue one. If it is first seen past the final stage, only the final reminder is drafted.",
      "A low-credit draft is made at most once a month per account.",
      "Drafts for accounts that are no longer overdue drop off the list after 90 days.",
    ],
    version: "v1.2",
    lastUpdated: "2026-10-27",
  },

  "/formulations": {
    title: "Official Formulations",
    description:
      "The register of production recipes. Each formulation has numbered versions, and each version is a list of materials in kilograms plus optional NFLG mixer timings and motor speeds. Read a version's bill of materials and mixer settings, write and save drafts, promote a draft to current, correct a current recipe with a stated reason, and copy a version into a new draft.",
    dataSources: [
      {
        name: "Formulations, versions and recipe lines",
        table: "production_monitor_product, production_monitor_formulation",
        source: "Cloud SQL (live)",
      },
      {
        name: "Materials catalog",
        source: "Cloud SQL (live)",
      },
      {
        name: "Change trail",
        table: "app_change_log",
        source: "Cloud SQL (live)",
      },
      {
        name: "NFLG mixing parameters",
        table: "production_monitor_formulation.mixing_*",
        source: "Cloud SQL (live; optional held migration)",
      },
      {
        name: "Masterbatch mark",
        table: "production_monitor_product.masterbatch_marked, production_monitor_material.masterbatch_product_id",
        source: "Cloud SQL (live; optional held migration)",
      },
    ],
    calculations: [
      {
        metric: "Line kg and total",
        formula: "each line exactly as R&D stored it, in kg; the total is the sum of the lines and is the batch",
        notes: "Nothing on the page scales a line. Lines are entered in kg and saved exactly as typed.",
      },
      {
        metric: "Line %",
        formula: "line kg ÷ recipe total × 100, in exact fixed-point arithmetic",
        notes: "With Decimals on Auto: four decimal places rounded half up (Weigh-Out uses the same rule at three), trailing zeros dropped (0.35, 47.3443, 100.0), and <0.0001 for a line too small to show. A fixed Decimals setting (0 to 4) rounds every % to exactly that many places.",
      },
      {
        metric: "Ostendo comparison",
        formula: "Ostendo's BOM and the plant recipe, each as kg per tonne of its own recipe total",
      },
      {
        metric: "NFLG mixing parameters",
        formula: "The optional T1-T5 times are seconds and Hz1-Hz5 are motor speeds in hertz, shown exactly as recorded",
        notes: "Blank means not set. The values are exposed on the version read for Approval Monitor; downtime arithmetic belongs to APPRDOWNTIME.",
      },
    ],
    limitations: [
      "A version the plant has produced batches against is locked: its lines cannot change, and a change needs a new version.",
      "Correcting a current recipe changes that version in place. The reason and the before and after are kept in its change trail.",
      "Drafts are not offered on Weigh-Out until promoted.",
      "Mixer settings are optional and remain blank until R&D records them; the ten fields are not defaulted or inferred.",
      "The Ostendo comparison puts both sides per tonne of their own recipe total, so it compares proportions: two recipes with the same shares at different batch sizes read as a match.",
      "A formulation R&D marks as a masterbatch before Ostendo has a code for it reads Ostendo code pending. Recipes can use it as a line, but BOM costing is keyed on Ostendo codes, so that line is not costed until the code is set.",
    ],
    version: "v2.2",
    lastUpdated: "2026-11-03",
  },

  "/tds": {
    title: "TDS Editor",
    description:
      "Write and maintain technical data sheets. Block-based editor with data fields that pull their values from live QC results, autosave with checkpoints, a full revision history with visual diffs, a publish gate that checks the sheet before it can go out, and a review due date on every issued sheet, with overdue sheets flagged in the product list.",
    dataSources: [
      {
        name: "TDS documents, drafts and revisions",
        source: "Application database (append-only revision history)",
      },
      {
        name: "Data field values",
        source: "Live QC and product data at the moment the field is placed or refreshed",
      },
      {
        name: "Templates",
        source: "Application database; template changes are new revisions, so existing drafts and published sheets never shift underneath you",
      },
      {
        name: "Formulation change dates",
        table: "production_monitor_formulation via production_monitor_batch (plant Postgres)",
        source: "The product's plant name patterns matched to its batches, then their formulations; drives the \"Since last formulation change\" window. When more than one plant product matches, the window is not offered and the products are named. Needs formulation read access as well as TDS access.",
        refresh: "Live when the product is opened",
      },
      {
        name: "Standards behind each claim",
        source:
          "The standards recorded on QC vs TDS, Claims vs tests. A claim with neither a standard nor a method shows a red dot and is listed under Gaps.",
        refresh: "When the editor is opened",
      },
      {
        name: "Review by dates",
        table: "tds_published_revision.review_due_on",
        source: "Set on the publish form when a sheet is issued; 12 months from the issue date unless the publisher picks another day",
      },
      {
        name: "QC limit change dates",
        source: "The product's QC spec settings in force (plant Postgres), dated by their last edit and the day they were last switched in; read beside the product list, so a plant outage costs only the change check",
        refresh: "Live when the editor is opened, at most every 10 minutes",
      },
    ],
    calculations: [
      {
        metric: "Field values",
        formula: "resolved from the linked product's current data and formatted per field type",
      },
      {
        metric: "QC statistic window",
        formula: "the last 12 months to today (Sydney calendar) unless the author picks since the last formulation change or custom dates; the window is frozen into the figure's evidence",
      },
      {
        metric: "Review due",
        formula: "the Review by date set when the sheet was issued (12 months from its issue date when none was stored), brought forward to the day the product's formula or QC limits changed, if that is after the sheet took effect. Overdue once that day has passed on the Sydney calendar; due on the day itself",
      },
    ],
    limitations: [
      "Every figure needs a reason of at least 8 characters before it can be used. The reason is kept in the document history.",
      "A publish refusal names the figures it objected to. Fix those and retry; it is not a save failure.",
      "Which published revision the website shows is managed separately from the internal revision head.",
      "If someone else has saved over your draft, autosave freezes rather than overwrite them. Reload the page to pick up the newer draft.",
      "Proof as PDF prints the draft as last saved, laid out as issuing would print it, with DRAFT on every page and no revision number. It issues and stores nothing, and waits while an edit is still saving. A figure with no value yet prints as \"No value yet\"; issuing still refuses one.",
      "Sheets imported from the 2024 archive have no review date; the first one comes with the first revision issued in the app. A withdrawn sheet has none either.",
      "Formula changes count towards review dates only for roles that can see formulations, so a TDS-only role can see a later date than a formulation reader for the same sheet.",
    ],
    version: "v1.3",
    lastUpdated: "2026-10-23",
  },

  "/kb": {
    title: "Knowledge Base",
    description:
      "Curated reference articles: product data, test procedures, standards, materials science, troubleshooting, process notes, business rules, statistics, common queries, quality and raw materials. Searchable here, and the same articles ground the AI Data Analyst's answers.",
    dataSources: [
      {
        name: "Articles",
        table: "kb_articles",
        source: "Application database, authored in-app",
      },
      {
        name: "Search misses",
        table: "kb_misses",
        source: "Zero-result searches, logged for gap analysis",
      },
    ],
    calculations: [],
    limitations: [
      "Search is keyword-based, so try a shorter or different term before concluding an article doesn't exist.",
      "An article here changes what the AI Data Analyst says, so keep articles current or delete them.",
      "Zero-result searches are logged, so a fruitless search still helps: it flags the gap for an author.",
    ],
    version: "v1.1",
    lastUpdated: "2026-09-30",
  },

  "/weigh-out": {
    title: "Weigh-Out",
    description:
      "Print-friendly weigh sheet for the plant floor. Pick a product and its current formulation version's recipe lines scale to batch kilograms, to the gram. There is no version picker: the sheet is always the current version. The batch size starts at the recipe's total, the sum of its lines as R&D stored them, so every line reads exactly as stored. Type a different batch (or open a link with one) and every line scales by its share of the recipe. Optional NFLG mixer timings and motor speeds show under the recipe. A line under the version name says when the recipe went into use and when its lines last changed, with a New tag while either is recent. Read-only: recipe changes happen in Formulations, not here.",
    dataSources: [
      {
        name: "Products and formulation versions",
        table: "production_monitor_product, production_monitor_formulation",
        source: "Cloud SQL (live)",
      },
      {
        name: "Recipe lines (kg)",
        table: "production_monitor_formulation material lines",
        source: "Cloud SQL (live)",
      },
      {
        name: "When the recipe went into use and when its lines last changed",
        table: "app_change_log (formulation changes in this environment)",
        source: "Cloud SQL (live)",
      },
      {
        name: "NFLG mixing parameters",
        table: "production_monitor_formulation.mixing_*",
        source: "Cloud SQL (live; optional held migration)",
      },
    ],
    calculations: [
      {
        metric: "kg for this batch, per line",
        formula: "line kg ÷ recipe total kg × batch size, computed in fixed-point (no floating-point rounding) and shown to the gram (at most three decimal places)",
        notes: "A recipe is stored as the kilograms of the batch R&D wrote it at, so each line is weighed as its share of the recipe; at the recipe's own total each line equals the stored kg. The batch is rounded to the gram and the lines always add up to it exactly, so one line can be a gram above or below its own rounding. The same shared engine computes what R&D authored and what the floor weighs, so the two can never drift.",
      },
      {
        metric: "%",
        formula: "line kg ÷ total recipe kg × 100",
        notes: "Exact, to three decimal places rounded half up, with trailing zeros dropped (48.904, 0.08, 100.0). A line too small to show at three places reads <0.001. Shown on screens from tablet width up and on the printed sheet; hidden on a phone.",
      },
      {
        metric: "In use since / lines last changed",
        formula: "In use since = the latest time the version was made current; lines last changed = the latest change to its lines made while it was current, after that",
        notes: "Both are factory days (Sydney time) and appear on the printed sheet. A version that was already current when the change trail started reads \"In use since before the change trail started\". Editing the weigh-out note or the R&D note changes neither date.",
      },
      {
        metric: "New tag",
        formula: "shown while either date is today or one of the 13 factory days before it",
        notes: "A recipe made current on the 1st shows New through the 14th and not on the 15th.",
      },
      {
        metric: "NFLG mixing parameters",
        formula: "Optional T1-T5 times in seconds and Hz1-Hz5 motor speeds in hertz, printed as recorded",
        notes: "Blank means not set. No downtime calculation is performed on this page.",
      },
    ],
    limitations: [
      "Only the current version is shown. Drafts and outdated versions cannot be weighed here; they stay on the Formulations page.",
      "A product with no current version shows no sheet.",
      "A current version without recorded mixer settings shows Mixing parameters not set.",
      "An old link naming a version (?version=) opens the current version and the version is dropped from the URL.",
      "The printed sheet reflects the batch size entered at print time, so reprint after changing it.",
      "If the change trail cannot be read, the line says the date could not be loaded and no New tag is shown.",
    ],
    version: "v1.3",
    lastUpdated: "2026-10-19",
  },

  "/quality?tab=stats": {
    title: "QC Stats",
    description:
      "Quality analytics over a chosen date range: plant performance (volume, on-hold and rejected tonnage, Quality Index), production composition by product, approval status, QC test usage, and testing frequency per product. The QC Flag Count Report (monthly red/amber flag counts per product, graded as the LIMS badges grade) is the second view on this tab.",
    dataSources: [
      {
        name: "AO tail production records (bag counts)",
        table: "qa_production_upi",
        source: "Cloud SQL (live)",
      },
      {
        name: "Bag weights",
        table: "qa_production_bagkg",
        source: "Cloud SQL (live)",
        refresh: "Falls back to a per-product default when no weight is recorded",
      },
      {
        name: "QC sample tests and spec bands",
        table: "qa_production_*sample tables + QA config",
        source: "Cloud SQL (live)",
      },
    ],
    calculations: [
      {
        metric: "Volume / On Hold / Rejected (ton)",
        formula: "bag count × bag weight ÷ 1000, per product, summed over the range",
      },
      {
        metric: "Quality Index",
        formula: "(volume − on hold − rejected) ÷ volume × 100",
      },
      {
        metric: "Tonnes per test",
        formula: "production volume for the product ÷ number of tests recorded in the range",
        notes: "Shown as a dash when a product's samples can't be matched to its production volume.",
      },
      {
        metric: "Yellow / Red counts",
        formula: "test results outside the warning / rejection band configured for that test",
      },
    ],
    limitations: [
      "Tonnage is derived from bag counts × bag weight, so a missing bag weight makes that product read as 0 tonnes.",
      "Products with samples but no matching production record show test counts without frequency.",
      "Wide date ranges scan every product's sample table and can be slow.",
    ],
    version: "v1.0",
    lastUpdated: "2026-08-04",
  },
};

/**
 * #729: resolve the documentation for a (path, tab) pair.
 *
 * Resolution order, most specific first:
 *   1. `"/canonical?tab=<tab>"` is the Tab-specific doc of a merged Page
 *   2. `"/canonical"`           is the whole-Page doc (exactly the pre-#729
 *                                 behaviour for pages without per-tab docs)
 *
 * The incoming pathname is canonicalised through the #719 alias map first, so
 * a doc keyed by the live path is still found when the caller passes a retired
 * path (an old bookmark hitting the client before the alias redirect, or a
 * non-router consumer such as the AI Data Analyst). An alias target may itself
 * carry a tab (e.g. "/waste" -> "/materials?tab=waste", live since #714);
 * an explicit `tab` argument wins over the alias-embedded one.
 *
 * `docs` / `aliases` are injectable for tests (same pattern as rbac.ts).
 */
export function resolvePageDoc(
  pathname: string,
  tab?: string | null,
  docs: Record<string, PageDocumentation> = PAGE_DOCS,
  aliases: Record<string, string> = PATH_ALIASES
): PageDocumentation | undefined {
  const canonical = canonicalPath(pathname, aliases);
  const [path, aliasQuery] = canonical.split("?");
  const aliasTab = aliasQuery ? new URLSearchParams(aliasQuery).get("tab") : null;
  const effectiveTab = tab || aliasTab;
  if (effectiveTab) {
    const tabDoc = docs[`${path}?tab=${effectiveTab}`];
    if (tabDoc) return tabDoc;
  }
  return docs[path];
}
